import React, { useState, useEffect, useCallback } from 'react';
import { useResumeRefresh } from '../hooks/useResumeRefresh';
import NotificationBell from '@/components/NotificationBell';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Building2, DollarSign, FileText, Download, 
  TrendingUp, Calendar, Trash2, Edit2, ChevronDown, PieChart,
  BarChart3, CheckCircle2, AlertCircle, Upload, Paperclip, X,
  Menu, User, Settings, LogOut, Landmark, RefreshCw, LayoutDashboard, HelpCircle
} from 'lucide-react';
import { formatDate, formatCurrentDate } from '@/lib/dateUtils';

// Alias for backwards compatibility
const formatDateMMDDYYYY = formatDate;

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';

// Import Plaid Link for bank connections
import { usePlaidLink } from 'react-plaid-link';

const PROPERTY_TYPES = {
  1: "Single Family Residence",
  2: "Multi-Family Residence",
  3: "Vacation/Short-Term Rental",
  4: "Commercial",
  5: "Land",
  6: "Royalties",
  7: "Self-Rental",
  8: "Other"
};

const EXPENSE_CATEGORY_LABELS = {
  advertising: "Advertising",
  auto_and_travel: "Auto and Travel",
  cleaning_and_maintenance: "Cleaning and Maintenance",
  commissions: "Commissions",
  insurance: "Insurance",
  legal_and_professional_fees: "Legal and Professional Fees",
  interest: "Interest (Mortgage)",
  mortgage_interest: "Mortgage Interest",
  mortgage_principal: "Mortgage Principal",
  property_taxes: "Property Taxes",
  hoa_fees: "HOA Fees",
  pmi: "PMI (Private Mortgage Insurance)",
  other_recurring: "Other Recurring Costs",
  repairs: "Repairs",
  taxes: "Taxes (Property)",
  utilities: "Utilities",
  wages_and_salaries: "Wages and Salaries",
  depreciation: "Depreciation",
  other: "Other"
};

const GENERAL_EXPENSE_LABELS = {
  office_supplies: "Office Supplies",
  fuel_auto: "Fuel & Auto",
  professional_services: "Professional Services",
  software_subscriptions: "Software & Subscriptions",
  phone_internet: "Phone & Internet",
  meals_entertainment: "Meals & Entertainment",
  travel: "Travel",
  education_training: "Education & Training",
  marketing: "Marketing",
  bank_fees: "Bank Fees",
  licenses_permits: "Licenses & Permits",
  other_business: "Other Business Expense"
};

const INCOME_TYPE_LABELS = {
  rent: "Rent Payment",
  property_sale: "Sale of Property",
  late_fees: "Late Fees",
  security_deposit_forfeited: "Security Deposit Forfeited",
  application_fees: "Application Fees",
  other_rental: "Other Rental Income",
  business_income: "Business Income",
  other: "Other Income"
};

function Reports({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('properties');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => { setMobileNavOpen(false); }, [activeTab]);
  const [properties, setProperties] = useState([]);
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [generalExpenses, setGeneralExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Dialog states
  const [isPropertyDialogOpen, setIsPropertyDialogOpen] = useState(false);
  const [isIncomeDialogOpen, setIsIncomeDialogOpen] = useState(false);
  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);
  const [isGeneralExpenseDialogOpen, setIsGeneralExpenseDialogOpen] = useState(false);
  const [isEditPropertyDialogOpen, setIsEditPropertyDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [selectedPropertyForExpense, setSelectedPropertyForExpense] = useState(null);
  
  // Per-Property Report states
  const [selectedPropertyForReport, setSelectedPropertyForReport] = useState(null);
  const [propertyReportData, setPropertyReportData] = useState(null);
  const [isPropertyReportDialogOpen, setIsPropertyReportDialogOpen] = useState(false);
  const [propertyReportYear, setPropertyReportYear] = useState(new Date().getFullYear());
  
  // Edit general expense states
  const [isEditGeneralExpenseDialogOpen, setIsEditGeneralExpenseDialogOpen] = useState(false);
  const [editingGeneralExpense, setEditingGeneralExpense] = useState(null);

  // Edit expense states
  const [isEditExpenseDialogOpen, setIsEditExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  
  // Monthly costs states
  const [monthlyCosts, setMonthlyCosts] = useState([]);
  const [isMonthlyCostDialogOpen, setIsMonthlyCostDialogOpen] = useState(false);
  const [selectedPropertyForMonthlyCost, setSelectedPropertyForMonthlyCost] = useState(null);
  const [newMonthlyCost, setNewMonthlyCost] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    principal: '',
    interest: '',
    insurance: '',
    property_tax: '',
    hoa: '',
    pmi: '',
    other: '',
    other_description: '',
    notes: ''
  });
  
  // Bank Import states
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [importMonth, setImportMonth] = useState(new Date().getMonth() + 1);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [mortgageGroups, setMortgageGroups] = useState([]);
  const [userBankAccount, setUserBankAccount] = useState(null);
  const [plaidLinkToken, setPlaidLinkToken] = useState(null);
  
  // Report states
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [selectedPeriod, setSelectedPeriod] = useState('yearly'); // yearly, quarterly, semi-annual, ytd, custom
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  // Business-expenses list controls (sort newest-first + filters)
  const [projects, setProjects] = useState([]);
  const [bizRange, setBizRange] = useState('all');
  const [bizProject, setBizProject] = useState('all');
  const [bizCustom, setBizCustom] = useState({ start: '', end: '' });
  // Shared Reports filters (date range + property) reused across tabs
  const [repRange, setRepRange] = useState('all');
  const [repProject, setRepProject] = useState('all');
  const [repCustom, setRepCustom] = useState({ start: '', end: '' });
  const [form8825Data, setForm8825Data] = useState(null);
  const [profitLossData, setProfitLossData] = useState(null);
  const [balanceSheetData, setBalanceSheetData] = useState(null);
  const [quarterlyData, setQuarterlyData] = useState(null);
  const [overallData, setOverallData] = useState(null);
  const [projectExpenses, setProjectExpenses] = useState([]);
  
  // Form states - Simplified property fields (expenses now tracked via Monthly Costs)
  const [newProperty, setNewProperty] = useState({
    address: '', city: '', state: '', zip_code: '',
    property_type: 1, purchase_price: '', purchase_date: '',
    fair_rental_days: 365, personal_use_days: 0, monthly_rent: '',
    home_value: '',  // Current market value
    loan_balance: '',  // Amount owed
    loan_number: '',  // For matching mortgage transactions
    lender_name: ''   // e.g., "Hills Bank", "Rocket Mortgage"
  });
  
  const [newIncome, setNewIncome] = useState({
    property_id: '', amount: '', income_type: 'rent', description: '', date: ''
  });
  
  const [newExpense, setNewExpense] = useState({
    property_id: '', amount: '', category: 'repairs', description: '', date: '',
    receipt_file: null, receipt_file_id: '', receipt_filename: ''
  });
  
  const [newGeneralExpense, setNewGeneralExpense] = useState({
    amount: '', category: 'office_supplies', description: '', date: ''
  });

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);
  useResumeRefresh(() => { if (user) loadData(); });

  const loadData = async () => {
    setLoading(true);
    try {
      const [propertiesRes, incomeRes, expensesRes, generalExpensesRes, monthlyCostsRes, projectsRes] = await Promise.all([
        apiService.getRentalProperties(user.id),
        apiService.getRentalIncome(user.id),
        apiService.getRentalExpenses(user.id),
        apiService.getGeneralExpenses(user.id),
        apiService.getMonthlyCosts(user.id),
        apiService.getProjects(user.id)  // Get construction projects
      ]);
      setProperties(propertiesRes.data);
      setIncome(incomeRes.data);
      setExpenses(expensesRes.data);
      setGeneralExpenses(generalExpensesRes.data);
      setProjects(projectsRes.data || []);
      setMonthlyCosts(monthlyCostsRes.data);
      
      // Calculate project expenses from all projects
      let allProjectExpenses = [];
      for (const project of projectsRes.data || []) {
        try {
          const phasesRes = await apiService.getPhases(project.id);
          for (const phase of phasesRes.data || []) {
            if (phase.expenses && phase.expenses.length > 0) {
              allProjectExpenses = allProjectExpenses.concat(
                phase.expenses.map(e => ({
                  ...e,
                  project_name: project.name,
                  project_id: project.id,
                  phase_name: phase.name
                }))
              );
            }
          }
        } catch (err) {
          console.error('Error loading project phases:', err);
        }
      }
      setProjectExpenses(allProjectExpenses);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Load user's bank account status
  const loadBankAccount = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await apiService.getUserBankAccount(user.id);
      setUserBankAccount(response.data.connected ? response.data : null);
    } catch (error) {
      console.error('Failed to load bank account:', error);
    }
  }, [user?.id]);

  // Initialize Plaid Link token
  const initPlaidLink = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await apiService.createPlaidLinkToken(user.id);
      setPlaidLinkToken(response.data.link_token);
    } catch (error) {
      console.error('Failed to create Plaid link token:', error);
    }
  }, [user?.id]);

  // Handle Plaid Link success
  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    try {
      toast.loading('Connecting your bank account...');
      const accountData = metadata.accounts[0];
      await apiService.exchangePlaidToken({
        user_id: user.id,
        public_token: publicToken,
        account_id: accountData.id,
        institution_name: metadata.institution?.name || 'Bank',
        account_name: accountData.name || 'Account',
        account_mask: accountData.mask || '****'
      });
      toast.dismiss();
      toast.success('Bank account connected!');
      await loadBankAccount();
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to connect bank account');
    }
  }, [user?.id, loadBankAccount]);

  // Plaid Link hook
  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
    token: plaidLinkToken,
    onSuccess: onPlaidSuccess,
  });

  // Fetch mortgage transactions for import
  const handleFetchTransactions = async () => {
    if (!userBankAccount) {
      toast.error('Please connect your bank account first');
      return;
    }
    
    setIsLoadingTransactions(true);
    try {
      const response = await apiService.getMortgageTransactions(user.id, importYear, importMonth);
      setMortgageGroups(response.data.mortgage_payment_groups || []);
      
      if (response.data.mortgage_payment_groups?.length === 0) {
        toast.info(`No mortgage payments found for ${importMonth}/${importYear}`);
      } else {
        toast.success(`Found ${response.data.mortgage_payment_groups.length} mortgage payment(s)`);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch transactions');
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  // Assign mortgage payment to property
  const handleAssignMortgage = async (group, propertyId) => {
    try {
      await apiService.assignMortgageToProperty({
        user_id: user.id,
        property_id: propertyId,
        year: importYear,
        month: importMonth,
        breakdown: group.breakdown
      });
      
      toast.success('Monthly cost imported successfully!');
      
      // Refresh data
      await loadData();
      
      // Update the group to show it's been assigned
      setMortgageGroups(prev => prev.map(g => 
        g.date === group.date ? { ...g, assigned: true, assigned_property_id: propertyId } : g
      ));
    } catch (error) {
      console.error('Error assigning mortgage:', error);
      toast.error('Failed to import monthly cost');
    }
  };

  useEffect(() => {
    loadBankAccount();
    // Bank connection (Plaid/ACH) is "Coming Soon" — do NOT auto-init the Plaid
    // link token (that fired errors on page load before ACH is configured).
    // initPlaidLink();
  }, [loadBankAccount]);

  const handleCreateProperty = async (e) => {
    e.preventDefault();
    try {
      await apiService.createRentalProperty({
        ...newProperty,
        user_id: user.id,
        purchase_price: parseFloat(newProperty.purchase_price) || 0,
        monthly_rent: parseFloat(newProperty.monthly_rent) || 0,
        monthly_principal_interest: parseFloat(newProperty.monthly_principal_interest) || 0,
        monthly_property_tax: parseFloat(newProperty.monthly_property_tax) || 0,
        monthly_insurance: parseFloat(newProperty.monthly_insurance) || 0,
        monthly_hoa: parseFloat(newProperty.monthly_hoa) || 0,
        monthly_pmi: parseFloat(newProperty.monthly_pmi) || 0,
        loan_balance: parseFloat(newProperty.loan_balance) || 0
      });
      toast.success('Property added successfully!');
      setIsPropertyDialogOpen(false);
      setNewProperty({
        address: '', city: '', state: '', zip_code: '',
        property_type: 1, purchase_price: '', purchase_date: '',
        fair_rental_days: 365, personal_use_days: 0, monthly_rent: '',
        monthly_principal_interest: '', monthly_property_tax: '',
        monthly_insurance: '', monthly_hoa: '', monthly_pmi: '',
        loan_balance: '', loan_number: '', lender_name: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to add property');
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Delete this property and all its records?')) return;
    try {
      await apiService.deleteRentalProperty(propertyId);
      toast.success('Property deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const handleEditProperty = (property) => {
    setEditingProperty({
      ...property,
      purchase_price: property.purchase_price || '',
      monthly_rent: property.monthly_rent || '',
      monthly_principal_interest: property.monthly_principal_interest || '',
      monthly_property_tax: property.monthly_property_tax || '',
      monthly_insurance: property.monthly_insurance || '',
      monthly_hoa: property.monthly_hoa || '',
      monthly_pmi: property.monthly_pmi || '',
      loan_balance: property.loan_balance || ''
    });
    setIsEditPropertyDialogOpen(true);
  };

  const handleUpdateProperty = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateRentalProperty(editingProperty.id, {
        ...editingProperty,
        user_id: user.id,
        purchase_price: parseFloat(editingProperty.purchase_price) || 0,
        monthly_rent: parseFloat(editingProperty.monthly_rent) || 0,
        monthly_principal_interest: parseFloat(editingProperty.monthly_principal_interest) || 0,
        monthly_property_tax: parseFloat(editingProperty.monthly_property_tax) || 0,
        monthly_insurance: parseFloat(editingProperty.monthly_insurance) || 0,
        monthly_hoa: parseFloat(editingProperty.monthly_hoa) || 0,
        monthly_pmi: parseFloat(editingProperty.monthly_pmi) || 0,
        loan_balance: parseFloat(editingProperty.loan_balance) || 0
      });
      toast.success('Property updated!');
      setIsEditPropertyDialogOpen(false);
      setEditingProperty(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update property');
    }
  };

  const handleCreateIncome = async (e) => {
    e.preventDefault();
    const date = new Date(newIncome.date);
    try {
      await apiService.createRentalIncome({
        ...newIncome,
        property_id: newIncome.property_id || null,  // null if no property selected
        user_id: user.id,
        amount: parseFloat(newIncome.amount),
        year: date.getFullYear(),
        quarter: Math.ceil((date.getMonth() + 1) / 3)
      });
      toast.success('Income recorded!');
      setIsIncomeDialogOpen(false);
      setNewIncome({ property_id: '', amount: '', income_type: 'rent', description: '', date: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to record income');
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    // Parse year directly from date string to avoid timezone issues
    const dateStr = newExpense.date;
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(5, 7));
    const quarter = Math.ceil(month / 3);
    const propertyId = selectedPropertyForExpense || newExpense.property_id;
    try {
      let receiptData = {};
      
      // Upload receipt if file is attached
      if (newExpense.receipt_file) {
        const uploadRes = await apiService.uploadExpenseReceipt(newExpense.receipt_file);
        receiptData = {
          receipt_file_id: uploadRes.data.file_id,
          receipt_filename: uploadRes.data.filename
        };
      }
      
      await apiService.createRentalExpense({
        property_id: propertyId,
        user_id: user.id,
        amount: parseFloat(newExpense.amount),
        category: newExpense.category,
        description: newExpense.description,
        date: newExpense.date,
        year: year,
        quarter: quarter,
        ...receiptData
      });
      toast.success('Expense recorded!');
      setIsExpenseDialogOpen(false);
      setSelectedPropertyForExpense(null);
      setNewExpense({ property_id: '', amount: '', category: 'repairs', description: '', date: '', receipt_file: null, receipt_file_id: '', receipt_filename: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to record expense');
      console.error(error);
    }
  };

  // Update expense handler
  const handleUpdateExpense = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateRentalExpense(editingExpense.id, {
        property_id: editingExpense.property_id,
        category: editingExpense.category,
        amount: parseFloat(editingExpense.amount),
        description: editingExpense.description,
        date: editingExpense.date
      });
      toast.success('Expense updated!');
      setIsEditExpenseDialogOpen(false);
      setEditingExpense(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update expense');
      console.error(error);
    }
  };

  // Monthly cost handlers
  const handleCreateMonthlyCost = async (e) => {
    e.preventDefault();
    if (!selectedPropertyForMonthlyCost) {
      toast.error('Please select a property');
      return;
    }
    try {
      await apiService.createMonthlyCost(user.id, {
        property_id: selectedPropertyForMonthlyCost,
        year: parseInt(newMonthlyCost.year),
        month: parseInt(newMonthlyCost.month),
        principal: parseFloat(newMonthlyCost.principal) || 0,
        interest: parseFloat(newMonthlyCost.interest) || 0,
        insurance: parseFloat(newMonthlyCost.insurance) || 0,
        property_tax: parseFloat(newMonthlyCost.property_tax) || 0,
        hoa: parseFloat(newMonthlyCost.hoa) || 0,
        pmi: parseFloat(newMonthlyCost.pmi) || 0,
        other: parseFloat(newMonthlyCost.other) || 0,
        other_description: newMonthlyCost.other_description,
        notes: newMonthlyCost.notes
      });
      toast.success('Monthly costs recorded!');
      setIsMonthlyCostDialogOpen(false);
      setSelectedPropertyForMonthlyCost(null);
      setNewMonthlyCost({
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        principal: '', interest: '', insurance: '', property_tax: '',
        hoa: '', pmi: '', other: '', other_description: '', notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to record monthly costs');
      console.error(error);
    }
  };

  // Handle update general expense
  const handleUpdateGeneralExpense = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateGeneralExpense(editingGeneralExpense.id, {
        category: editingGeneralExpense.category,
        amount: parseFloat(editingGeneralExpense.amount),
        description: editingGeneralExpense.description,
        date: editingGeneralExpense.date
      });
      toast.success('Expense updated!');
      setIsEditGeneralExpenseDialogOpen(false);
      setEditingGeneralExpense(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update expense');
      console.error(error);
    }
  };

  // Generate Per-Property Report
  const generatePropertyReport = async (propertyId) => {
    try {
      const property = properties.find(p => p.id === propertyId);
      if (!property) return;
      
      setSelectedPropertyForReport(property);
      
      // Get profit/loss data for this property
      let plResponse;
      try {
        plResponse = await apiService.getProfitLoss(user.id, propertyReportYear, null, propertyId);
      } catch (err) {
        console.error('Profit/Loss API error:', err);
        plResponse = { data: { total_income: 0, total_expenses: 0, net_income: 0, income_breakdown: {}, expenses_breakdown: {} } };
      }
      
      // Get income records for this property - filter by year from date field
      const propertyIncome = income.filter(i => {
        if (i.property_id !== propertyId) return false;
        const recordYear = i.year || (i.date ? new Date(i.date).getFullYear() : null);
        return recordYear === propertyReportYear;
      });
      
      // Get expense records for this property - filter by year from date field
      const propertyExpenses = expenses.filter(e => {
        if (e.property_id !== propertyId) return false;
        const recordYear = e.year || (e.date ? new Date(e.date).getFullYear() : null);
        return recordYear === propertyReportYear;
      });
      
      // Calculate totals from actual records if API doesn't return them
      const calculatedTotalIncome = propertyIncome.reduce((sum, inc) => sum + (parseFloat(inc.amount) || 0), 0);
      const calculatedTotalExpenses = propertyExpenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
      
      // Also add monthly costs (mortgage, insurance, taxes, etc.)
      const monthlyCosts = await loadMonthlyCostsForProperty(propertyId, propertyReportYear);
      const totalMonthlyCosts = monthlyCosts.reduce((sum, mc) => {
        return sum + (parseFloat(mc.principal) || 0) + (parseFloat(mc.interest) || 0) + 
               (parseFloat(mc.insurance) || 0) + (parseFloat(mc.property_tax) || 0) +
               (parseFloat(mc.hoa) || 0) + (parseFloat(mc.pmi) || 0) + (parseFloat(mc.other) || 0);
      }, 0);
      
      // Calculate expected annual rent income (if no income records, estimate from monthly rent)
      const monthsInYear = propertyReportYear === new Date().getFullYear() ? new Date().getMonth() + 1 : 12;
      const estimatedRentIncome = (property.monthly_rent || 0) * monthsInYear;
      
      // Use API data if available, otherwise use calculated, with estimated rent as fallback
      const totalIncome = plResponse.data.total_income > 0 
        ? plResponse.data.total_income 
        : calculatedTotalIncome > 0 
          ? calculatedTotalIncome 
          : estimatedRentIncome;
      const totalExpenses = (plResponse.data.total_expenses > 0 ? plResponse.data.total_expenses : calculatedTotalExpenses) + totalMonthlyCosts;
      const netIncome = totalIncome - totalExpenses;
      
      // Build expense breakdown from records
      const expenseBreakdown = {};
      propertyExpenses.forEach(exp => {
        const category = exp.category || 'other';
        expenseBreakdown[category] = (expenseBreakdown[category] || 0) + (parseFloat(exp.amount) || 0);
      });
      
      // Add all monthly/recurring costs to expense breakdown
      if (totalMonthlyCosts > 0) {
        const principalTotal = monthlyCosts.reduce((sum, mc) => sum + (parseFloat(mc.principal) || 0), 0);
        const interestTotal = monthlyCosts.reduce((sum, mc) => sum + (parseFloat(mc.interest) || 0), 0);
        const taxesTotal = monthlyCosts.reduce((sum, mc) => sum + (parseFloat(mc.property_tax) || 0), 0);
        const insuranceTotal = monthlyCosts.reduce((sum, mc) => sum + (parseFloat(mc.insurance) || 0), 0);
        const hoaTotal = monthlyCosts.reduce((sum, mc) => sum + (parseFloat(mc.hoa) || 0), 0);
        const pmiTotal = monthlyCosts.reduce((sum, mc) => sum + (parseFloat(mc.pmi) || 0), 0);
        const otherTotal = monthlyCosts.reduce((sum, mc) => sum + (parseFloat(mc.other) || 0), 0);
        
        // P&I combined or separate
        if (principalTotal > 0 || interestTotal > 0) {
          expenseBreakdown['mortgage_principal'] = principalTotal;
          expenseBreakdown['mortgage_interest'] = interestTotal;
        }
        if (taxesTotal > 0) expenseBreakdown['property_taxes'] = taxesTotal;
        if (insuranceTotal > 0) expenseBreakdown['insurance'] = insuranceTotal;
        if (hoaTotal > 0) expenseBreakdown['hoa_fees'] = hoaTotal;
        if (pmiTotal > 0) expenseBreakdown['pmi'] = pmiTotal;
        if (otherTotal > 0) expenseBreakdown['other_recurring'] = otherTotal;
      }
      
      setPropertyReportData({
        property,
        year: propertyReportYear,
        profitLoss: {
          ...plResponse.data,
          // Merge API expense breakdown with our calculated breakdown (including monthly costs)
          expenses_breakdown: {
            ...(plResponse.data.expenses_breakdown || {}),
            ...expenseBreakdown
          }
        },
        incomeRecords: propertyIncome,
        expenseRecords: propertyExpenses,
        monthlyCosts: monthlyCosts,
        totalIncome,
        totalExpenses,
        netIncome
      });
      
      setIsPropertyReportDialogOpen(true);
    } catch (error) {
      console.error('Failed to generate property report:', error);
      toast.error('Failed to generate property report');
    }
  };

  // Download Property Report as CSV
  const downloadPropertyReport = () => {
    if (!propertyReportData || !selectedPropertyForReport) return;
    
    const property = selectedPropertyForReport;
    const data = propertyReportData;
    const year = propertyReportYear;
    
    // Helper to format currency for CSV (quoted to handle commas)
    const formatCurrency = (amount) => `"$${(amount || 0).toLocaleString()}"`;
    
    // Build CSV content
    let csvContent = '';
    
    // Header
    csvContent += `PROPERTY FINANCIAL REPORT - ${year}\n`;
    csvContent += `Generated: ${formatCurrentDate()}\n\n`;
    
    // Property Info
    csvContent += `PROPERTY INFORMATION\n`;
    csvContent += `Address,"${property.address}, ${property.city}, ${property.state} ${property.zip_code}"\n`;
    csvContent += `Purchase Price,${formatCurrency(property.purchase_price)}\n`;
    csvContent += `Current Value,${formatCurrency(property.home_value)}\n`;
    csvContent += `Monthly Rent,${formatCurrency(property.monthly_rent)}\n`;
    csvContent += `Equity,${formatCurrency((property.home_value || 0) - (property.loan_balance || 0))}\n\n`;
    
    // Financial Summary
    csvContent += `FINANCIAL SUMMARY\n`;
    csvContent += `Total Income,${formatCurrency(data.totalIncome)}\n`;
    csvContent += `Total Expenses,${formatCurrency(data.totalExpenses)}\n`;
    csvContent += `Net Income,${formatCurrency(data.netIncome)}\n\n`;
    
    // Income Breakdown
    csvContent += `INCOME BREAKDOWN\n`;
    if (data.profitLoss?.income_breakdown) {
      Object.entries(data.profitLoss.income_breakdown).forEach(([type, amount]) => {
        csvContent += `"${type.replace(/_/g, ' ')}",${formatCurrency(amount)}\n`;
      });
    } else if (data.totalIncome > 0) {
      csvContent += `Rental Income (Estimated),${formatCurrency(data.totalIncome)}\n`;
    }
    csvContent += '\n';
    
    // Monthly Recurring Costs
    if (data.monthlyCosts && data.monthlyCosts.length > 0) {
      csvContent += `MONTHLY RECURRING COSTS (${data.monthlyCosts.length} months)\n`;
      
      const totals = data.monthlyCosts.reduce((acc, mc) => ({
        principal: acc.principal + (parseFloat(mc.principal) || 0),
        interest: acc.interest + (parseFloat(mc.interest) || 0),
        property_tax: acc.property_tax + (parseFloat(mc.property_tax) || 0),
        insurance: acc.insurance + (parseFloat(mc.insurance) || 0),
        hoa: acc.hoa + (parseFloat(mc.hoa) || 0),
        pmi: acc.pmi + (parseFloat(mc.pmi) || 0),
        other: acc.other + (parseFloat(mc.other) || 0)
      }), { principal: 0, interest: 0, property_tax: 0, insurance: 0, hoa: 0, pmi: 0, other: 0 });
      
      if (totals.principal > 0) csvContent += `Mortgage Principal,${formatCurrency(totals.principal)}\n`;
      if (totals.interest > 0) csvContent += `Mortgage Interest,${formatCurrency(totals.interest)}\n`;
      if (totals.property_tax > 0) csvContent += `Property Taxes,${formatCurrency(totals.property_tax)}\n`;
      if (totals.insurance > 0) csvContent += `Insurance,${formatCurrency(totals.insurance)}\n`;
      if (totals.hoa > 0) csvContent += `HOA Fees,${formatCurrency(totals.hoa)}\n`;
      if (totals.pmi > 0) csvContent += `PMI,${formatCurrency(totals.pmi)}\n`;
      if (totals.other > 0) csvContent += `Other,${formatCurrency(totals.other)}\n`;
      
      const totalRecurring = totals.principal + totals.interest + totals.property_tax + totals.insurance + totals.hoa + totals.pmi + totals.other;
      csvContent += `Total Recurring Costs,${formatCurrency(totalRecurring)}\n\n`;
    }
    
    // Other Expenses
    csvContent += `OTHER EXPENSES\n`;
    if (data.profitLoss?.expenses_breakdown) {
      const recurringKeys = ['mortgage_principal', 'mortgage_interest', 'property_taxes', 'insurance', 'hoa_fees', 'pmi', 'other_recurring'];
      Object.entries(data.profitLoss.expenses_breakdown)
        .filter(([category]) => !recurringKeys.includes(category))
        .forEach(([category, amount]) => {
          csvContent += `"${EXPENSE_CATEGORY_LABELS[category] || category}",${formatCurrency(amount)}\n`;
        });
    }
    csvContent += '\n';
    
    // Recent Expense Transactions
    if (data.expenseRecords && data.expenseRecords.length > 0) {
      csvContent += `EXPENSE TRANSACTIONS\n`;
      csvContent += `Date,Category,Description,Amount\n`;
      data.expenseRecords.slice(0, 20).forEach(exp => {
        csvContent += `${formatDateMMDDYYYY(exp.date)},"${EXPENSE_CATEGORY_LABELS[exp.category] || exp.category}","${exp.description || ''}",${formatCurrency(exp.amount)}\n`;
      });
    }
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Property_Report_${property.address.replace(/[^a-zA-Z0-9]/g, '_')}_${year}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Property report downloaded!');
  };

  // Helper to load monthly costs for a specific property and year
  const loadMonthlyCostsForProperty = async (propertyId, year) => {
    try {
      // Pass propertyId and year correctly to the API
      const response = await apiService.getMonthlyCosts(user.id, propertyId, year);
      return response.data || [];
    } catch (error) {
      console.error('Failed to load monthly costs:', error);
      return [];
    }
  };

  const getMonthName = (month) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1] || '';
  };

  // Get date range based on selected period
  const getDateRange = () => {
    const year = selectedYear;
    const today = new Date();
    let startDate, endDate, periodLabel;
    
    switch (selectedPeriod) {
      case 'yearly':
        startDate = `${year}-01-01`;
        endDate = `${year}-12-31`;
        periodLabel = `Full Year ${year}`;
        break;
      case 'quarterly':
        const qStart = (selectedQuarter - 1) * 3 + 1;
        startDate = `${year}-${String(qStart).padStart(2, '0')}-01`;
        const qEndMonth = qStart + 2;
        const qEndDay = new Date(year, qEndMonth, 0).getDate();
        endDate = `${year}-${String(qEndMonth).padStart(2, '0')}-${qEndDay}`;
        periodLabel = `Q${selectedQuarter} ${year}`;
        break;
      case 'semi-annual':
        if (selectedQuarter <= 2) {
          startDate = `${year}-01-01`;
          endDate = `${year}-06-30`;
          periodLabel = `H1 ${year} (Jan-Jun)`;
        } else {
          startDate = `${year}-07-01`;
          endDate = `${year}-12-31`;
          periodLabel = `H2 ${year} (Jul-Dec)`;
        }
        break;
      case 'ytd':
        startDate = `${year}-01-01`;
        endDate = today.toISOString().split('T')[0];
        periodLabel = `YTD ${year} (Jan - ${getMonthName(today.getMonth() + 1)})`;
        break;
      case 'custom':
        startDate = customStartDate || `${year}-01-01`;
        endDate = customEndDate || today.toISOString().split('T')[0];
        periodLabel = `Custom: ${formatDateMMDDYYYY(startDate)} - ${formatDateMMDDYYYY(endDate)}`;
        break;
      default:
        startDate = `${year}-01-01`;
        endDate = `${year}-12-31`;
        periodLabel = `Full Year ${year}`;
    }
    
    return { startDate, endDate, periodLabel };
  };

  // Filter data by date range
  const filterByDateRange = (items, dateField = 'date') => {
    const { startDate, endDate } = getDateRange();
    return items.filter(item => {
      const itemDate = item[dateField];
      return itemDate >= startDate && itemDate <= endDate;
    });
  };

  const handleCreateGeneralExpense = async (e) => {
    e.preventDefault();
    const date = new Date(newGeneralExpense.date);
    try {
      await apiService.createGeneralExpense({
        ...newGeneralExpense,
        user_id: user.id,
        amount: parseFloat(newGeneralExpense.amount),
        year: date.getFullYear(),
        quarter: Math.ceil((date.getMonth() + 1) / 3)
      });
      toast.success('Business expense recorded!');
      setIsGeneralExpenseDialogOpen(false);
      setNewGeneralExpense({ amount: '', category: 'office_supplies', description: '', date: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to record expense');
    }
  };

  // Business expenses view: newest first, filtered by date range + project.
  const getBizRange = () => {
    const now = new Date();
    const y = now.getFullYear();
    const iso = (d) => d.toISOString().split('T')[0];
    switch (bizRange) {
      case 'month': return { start: iso(new Date(y, now.getMonth(), 1)), end: iso(new Date(y, now.getMonth() + 1, 0)) };
      case 'quarter': { const q = Math.floor(now.getMonth() / 3); return { start: iso(new Date(y, q * 3, 1)), end: iso(new Date(y, q * 3 + 3, 0)) }; }
      case 'year': return { start: `${y}-01-01`, end: `${y}-12-31` };
      case 'lastyear': return { start: `${y - 1}-01-01`, end: `${y - 1}-12-31` };
      case 'custom': return { start: bizCustom.start || '0000-01-01', end: bizCustom.end || '9999-12-31' };
      default: return { start: '0000-01-01', end: '9999-12-31' };
    }
  };
  const visibleBusinessExpenses = [...generalExpenses]
    .filter((it) => {
      const { start, end } = getBizRange();
      const d = (it.date || '').slice(0, 10);
      if (d && (d < start || d > end)) return false;
      if (bizProject !== 'all' && (it.project_id || '') !== bizProject) return false;
      return true;
    })
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  // Loan balance on the Properties tab reduces by the principal paid via Monthly Costs.
  const principalPaidFor = (pid) => monthlyCosts
    .filter((mc) => mc.property_id === pid)
    .reduce((s, mc) => s + (parseFloat(mc.principal) || 0), 0);
  const currentLoanBalance = (prop) => Math.max((parseFloat(prop.loan_balance) || 0) - principalPaidFor(prop.id), 0);

  // Shared filter used by the Properties / Monthly Costs / Income / Expenses tabs.
  const repDateRange = () => {
    const now = new Date();
    const y = now.getFullYear();
    const iso = (d) => d.toISOString().split('T')[0];
    switch (repRange) {
      case 'month': return { start: iso(new Date(y, now.getMonth(), 1)), end: iso(new Date(y, now.getMonth() + 1, 0)) };
      case 'quarter': { const q = Math.floor(now.getMonth() / 3); return { start: iso(new Date(y, q * 3, 1)), end: iso(new Date(y, q * 3 + 3, 0)) }; }
      case 'year': return { start: `${y}-01-01`, end: `${y}-12-31` };
      case 'lastyear': return { start: `${y - 1}-01-01`, end: `${y - 1}-12-31` };
      case 'custom': return { start: repCustom.start || '0000-01-01', end: repCustom.end || '9999-12-31' };
      default: return { start: '0000-01-01', end: '9999-12-31' };
    }
  };
  const applyRep = (list, { byProperty = true } = {}) => {
    const { start, end } = repDateRange();
    return [...(list || [])]
      .filter((it) => {
        const d = (it.date || '').slice(0, 10);
        if (d && (d < start || d > end)) return false;
        if (byProperty && repProject !== 'all' && (it.property_id || '') !== repProject) return false;
        return true;
      })
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  };
  const visibleProperties = repProject === 'all' ? properties : properties.filter((p) => p.id === repProject);
  const renderRepFilters = (showProject = true) => (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <Select value={repRange} onValueChange={setRepRange}>
        <SelectTrigger className="w-[150px]" data-testid="rep-range"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Time</SelectItem>
          <SelectItem value="month">This Month</SelectItem>
          <SelectItem value="quarter">This Quarter</SelectItem>
          <SelectItem value="year">This Year</SelectItem>
          <SelectItem value="lastyear">Last Year</SelectItem>
          <SelectItem value="custom">Custom</SelectItem>
        </SelectContent>
      </Select>
      {showProject && (
        <Select value={repProject} onValueChange={setRepProject}>
          <SelectTrigger className="w-[180px]" data-testid="rep-property"><SelectValue placeholder="All Properties" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Properties</SelectItem>
            {properties.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name || p.address || 'Property'}</SelectItem>))}
          </SelectContent>
        </Select>
      )}
      {repRange === 'custom' && (
        <>
          <Input type="date" value={repCustom.start} onChange={(e) => setRepCustom((s) => ({ ...s, start: e.target.value }))} className="w-[150px]" data-testid="rep-custom-start" />
          <Input type="date" value={repCustom.end} onChange={(e) => setRepCustom((s) => ({ ...s, end: e.target.value }))} className="w-[150px]" data-testid="rep-custom-end" />
        </>
      )}
    </div>
  );

  const generateForm8825 = async () => {
    try {
      const res = await apiService.getForm8825(user.id, selectedYear);
      setForm8825Data(res.data);
      toast.success('Form 8825 generated!');
    } catch (error) {
      toast.error('Failed to generate Form 8825');
    }
  };

  const generateProfitLoss = async () => {
    try {
      // Only pass quarter if quarterly period is selected
      const quarter = selectedPeriod === 'quarterly' ? selectedQuarter : null;
      const res = await apiService.getProfitLoss(user.id, selectedYear, quarter);
      setProfitLossData({
        ...res.data,
        period_label: getDateRange().periodLabel
      });
      toast.success('P&L Statement generated!');
    } catch (error) {
      toast.error('Failed to generate P&L');
    }
  };

  const generateBalanceSheet = async () => {
    try {
      const res = await apiService.getBalanceSheet(user.id);
      setBalanceSheetData(res.data);
      toast.success('Balance Sheet generated!');
    } catch (error) {
      toast.error('Failed to generate Balance Sheet');
    }
  };

  const generateQuarterly = async () => {
    try {
      // For quarterly report, use quarter if quarterly/semi-annual selected, otherwise get all quarters
      const quarter = (selectedPeriod === 'quarterly' || selectedPeriod === 'semi-annual') ? selectedQuarter : null;
      const res = await apiService.getQuarterlyReport(user.id, selectedYear, quarter);
      setQuarterlyData({
        ...res.data,
        period_label: getDateRange().periodLabel
      });
      toast.success('Quarterly report generated!');
    } catch (error) {
      toast.error('Failed to generate quarterly report');
    }
  };

  const generateOverall = async () => {
    try {
      const res = await apiService.getOverallReport(user.id, selectedYear);
      setOverallData({
        ...res.data,
        period_label: getDateRange().periodLabel
      });
      toast.success('Overall report generated!');
    } catch (error) {
      toast.error('Failed to generate overall report');
    }
  };

  // Download a branded PDF blob returned by the backend report endpoints.
  const downloadPdf = async (promise, filename) => {
    try {
      toast.loading('Building branded PDF…', { id: 'pdf' });
      const res = await promise;
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded!', { id: 'pdf' });
    } catch (e) {
      toast.error('Could not generate PDF', { id: 'pdf' });
    }
  };

  const exportToCsv = (data, filename, type = 'csv') => {
    let content = '';
    let mimeType = 'text/csv';
    let extension = 'csv';
    
    const formatMoney = (val) => {
      const num = parseFloat(val) || 0;
      return `$${num.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    };
    
    // Determine report type and format accordingly
    if (data.totals && data.properties && data.totals.total_gross_rents !== undefined) {
      // Form 8825 Report
      content = '════════════════════════════════════════════════════════════════\n';
      content += '                    IRS FORM 8825 DATA\n';
      content += '           Rental Real Estate Income and Expenses\n';
      content += '════════════════════════════════════════════════════════════════\n\n';
      content += `Report Period: ${data.period_label || `Year ${data.year}`}\n`;
      content += `Generated: ${formatCurrentDate()}\n`;
      content += `Number of Properties: ${data.properties?.length || 0}\n\n`;
      
      content += '────────────────────────────────────────────────────────────────\n';
      content += 'PROPERTY DETAILS\n';
      content += '────────────────────────────────────────────────────────────────\n\n';
      
      data.properties?.forEach((p, i) => {
        content += `Property ${i + 1}: ${p.address}\n`;
        content += `  Type: ${p.property_type}\n`;
        content += `  Fair Rental Days: ${p.fair_rental_days || 365}\n`;
        content += `  Gross Rents: ${formatMoney(p.gross_rents)}\n`;
        content += `  Total Expenses: ${formatMoney(p.total_expenses)}\n`;
        content += `  Net Income: ${formatMoney(p.net_income)}\n\n`;
      });
      
      content += '────────────────────────────────────────────────────────────────\n';
      content += 'TOTALS\n';
      content += '────────────────────────────────────────────────────────────────\n';
      content += `Total Gross Rents: ${formatMoney(data.totals?.total_gross_rents)}\n`;
      content += `Total Expenses: ${formatMoney(data.totals?.total_expenses)}\n`;
      content += `Net Rental Income: ${formatMoney(data.totals?.net_rental_income)}\n`;
      
    } else if (data.net_profit !== undefined && data.income?.by_type) {
      // P&L Statement
      content = '════════════════════════════════════════════════════════════════\n';
      content += '                 PROFIT & LOSS STATEMENT\n';
      content += '════════════════════════════════════════════════════════════════\n\n';
      content += `Report Period: ${data.period_name || data.period_label || `Year ${data.year}`}\n`;
      content += `Generated: ${formatCurrentDate()}\n\n`;
      
      content += '────────────────────────────────────────────────────────────────\n';
      content += 'INCOME\n';
      content += '────────────────────────────────────────────────────────────────\n';
      
      // Income breakdown by type
      const incomeByType = data.income?.by_type || {};
      Object.entries(incomeByType).forEach(([type, amount]) => {
        const label = type === 'estimated_rent' ? 'Estimated Rental Income' :
                     type === 'rent' ? 'Recorded Rental Income' :
                     type === 'property_sale' ? 'Property Sale Proceeds' :
                     type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        content += `  ${label}: ${formatMoney(amount)}\n`;
      });
      content += `\n  TOTAL INCOME: ${formatMoney(data.income?.total)}\n\n`;
      
      content += '────────────────────────────────────────────────────────────────\n';
      content += 'EXPENSES\n';
      content += '────────────────────────────────────────────────────────────────\n';
      
      // Expense breakdown by category
      const expensesByCategory = data.expenses?.by_category || {};
      Object.entries(expensesByCategory).forEach(([cat, amount]) => {
        if (amount > 0) {
          const label = cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          content += `  ${label}: ${formatMoney(amount)}\n`;
        }
      });
      
      // Monthly costs breakdown
      const monthlyCosts = data.expenses?.monthly_costs || {};
      if (data.expenses?.monthly_costs_total > 0) {
        content += `\n  Monthly Costs (${data.expenses?.months_recorded || 0} months recorded):\n`;
        if (monthlyCosts.principal > 0) content += `    Principal: ${formatMoney(monthlyCosts.principal)}\n`;
        if (monthlyCosts.interest > 0) content += `    Interest: ${formatMoney(monthlyCosts.interest)}\n`;
        if (monthlyCosts.insurance > 0) content += `    Insurance: ${formatMoney(monthlyCosts.insurance)}\n`;
        if (monthlyCosts.property_tax > 0) content += `    Property Tax: ${formatMoney(monthlyCosts.property_tax)}\n`;
        if (monthlyCosts.hoa > 0) content += `    HOA: ${formatMoney(monthlyCosts.hoa)}\n`;
        if (monthlyCosts.pmi > 0) content += `    PMI: ${formatMoney(monthlyCosts.pmi)}\n`;
        if (monthlyCosts.other > 0) content += `    Other: ${formatMoney(monthlyCosts.other)}\n`;
      }
      
      content += `\n  TOTAL EXPENSES: ${formatMoney(data.expenses?.total)}\n\n`;
      
      content += '────────────────────────────────────────────────────────────────\n';
      content += 'SUMMARY\n';
      content += '────────────────────────────────────────────────────────────────\n';
      content += `NET PROFIT/LOSS: ${formatMoney(data.net_profit)}\n`;
      content += `Profit Margin: ${data.profit_margin || 0}%\n`;
      
    } else if (data.income && data.expenses && data.net_income !== undefined) {
      // Overall Report
      content = '════════════════════════════════════════════════════════════════\n';
      content += '              OVERALL BUSINESS REPORT\n';
      content += '════════════════════════════════════════════════════════════════\n\n';
      content += `Report Period: ${data.period_label || `Year ${data.year}`}\n`;
      content += `Generated: ${formatCurrentDate()}\n`;
      content += `Number of Properties: ${data.property_count || 0}\n\n`;
      
      content += '────────────────────────────────────────────────────────────────\n';
      content += 'INCOME\n';
      content += '────────────────────────────────────────────────────────────────\n';
      content += `  Gross Rental Income: ${formatMoney(data.income?.gross_annual_rent)}\n`;
      if (data.income?.recorded_income > 0) {
        content += `  Other Income (Sales, Fees): ${formatMoney(data.income?.recorded_income)}\n`;
      }
      content += `\n  TOTAL INCOME: ${formatMoney(data.income?.total)}\n\n`;
      
      content += '────────────────────────────────────────────────────────────────\n';
      content += 'EXPENSES\n';
      content += '────────────────────────────────────────────────────────────────\n';
      if (data.expenses?.recurring_property_costs?.total > 0) {
        content += `  Monthly Recurring Costs: ${formatMoney(data.expenses?.recurring_property_costs?.total)}\n`;
      }
      if (data.expenses?.rental_expenses?.total > 0) {
        content += `  Property Expenses: ${formatMoney(data.expenses?.rental_expenses?.total)}\n`;
      }
      if (data.expenses?.general_expenses?.total > 0) {
        content += `  Business Expenses: ${formatMoney(data.expenses?.general_expenses?.total)}\n`;
      }
      content += `\n  TOTAL EXPENSES: ${formatMoney(data.expenses?.total)}\n\n`;
      
      content += '────────────────────────────────────────────────────────────────\n';
      content += 'SUMMARY\n';
      content += '────────────────────────────────────────────────────────────────\n';
      content += `NET INCOME: ${formatMoney(data.net_income)}\n`;
      content += `Profit Margin: ${data.profit_margin || 0}%\n`;
      
    } else if (data.assets) {
      // Balance Sheet
      content = '════════════════════════════════════════════════════════════════\n';
      content += '                      BALANCE SHEET\n';
      content += '════════════════════════════════════════════════════════════════\n\n';
      content += `Generated: ${formatCurrentDate()}\n`;
      content += `Number of Properties: ${data.property_count || 0}\n\n`;
      
      content += '────────────────────────────────────────────────────────────────\n';
      content += 'ASSETS\n';
      content += '────────────────────────────────────────────────────────────────\n';
      content += `  Real Estate Value: ${formatMoney(data.assets?.real_estate)}\n`;
      content += `  Accumulated Depreciation: ${formatMoney(data.assets?.accumulated_depreciation)}\n`;
      content += `  Net Property Value: ${formatMoney(data.assets?.net_property_value)}\n\n`;
      
      content += '────────────────────────────────────────────────────────────────\n';
      content += 'LIABILITIES\n';
      content += '────────────────────────────────────────────────────────────────\n';
      content += `  Mortgages/Loans: ${formatMoney(data.liabilities?.mortgages_loans)}\n`;
      content += `  Total Liabilities: ${formatMoney(data.total_liabilities)}\n\n`;
      
      content += '────────────────────────────────────────────────────────────────\n';
      content += 'EQUITY\n';
      content += '────────────────────────────────────────────────────────────────\n';
      content += `  Retained Earnings: ${formatMoney(data.equity?.retained_earnings)}\n`;
      content += `  Total Equity: ${formatMoney(data.equity?.total_equity)}\n\n`;
      
      content += '────────────────────────────────────────────────────────────────\n';
      content += 'NET WORTH\n';
      content += '────────────────────────────────────────────────────────────────\n';
      content += `  NET WORTH: ${formatMoney(data.net_worth)}\n\n`;
      
      // Property details
      if (data.property_details?.length > 0) {
        content += '────────────────────────────────────────────────────────────────\n';
        content += 'PROPERTY DETAILS\n';
        content += '────────────────────────────────────────────────────────────────\n';
        data.property_details.forEach((p, i) => {
          content += `\nProperty ${i + 1}: ${p.address}\n`;
          content += `  Home Value: ${formatMoney(p.home_value)}\n`;
          content += `  Loan Balance: ${formatMoney(p.loan_balance)}\n`;
          content += `  Equity: ${formatMoney(p.equity)}\n`;
        });
      }
      
    } else if (data.summary && data.quarter_name) {
      // Quarterly Report
      content = '════════════════════════════════════════════════════════════════\n';
      content += '              QUARTERLY COMPILE STATEMENT\n';
      content += '════════════════════════════════════════════════════════════════\n\n';
      content += `Report Period: ${data.quarter_name || data.period_label}\n`;
      content += `Generated: ${formatCurrentDate()}\n\n`;
      
      content += '────────────────────────────────────────────────────────────────\n';
      content += 'PROPERTY BREAKDOWN\n';
      content += '────────────────────────────────────────────────────────────────\n';
      
      data.properties?.forEach((p, i) => {
        content += `\nProperty ${i + 1}: ${p.property}\n`;
        content += `  Recorded Income: ${formatMoney(p.recorded_income)}\n`;
        content += `  Estimated Rent: ${formatMoney(p.estimated_rent)}\n`;
        content += `  Total Income: ${formatMoney(p.income)}\n`;
        content += `  Expenses: ${formatMoney(p.expenses)}\n`;
        content += `  Net: ${formatMoney(p.net)}\n`;
      });
      
      content += '\n────────────────────────────────────────────────────────────────\n';
      content += 'SUMMARY\n';
      content += '────────────────────────────────────────────────────────────────\n';
      content += `Total Income: ${formatMoney(data.summary?.total_income)}\n`;
      content += `Total Expenses: ${formatMoney(data.summary?.total_expenses)}\n`;
      content += `Net Income: ${formatMoney(data.summary?.net_income)}\n`;
      
    } else {
      // Generic fallback
      content = 'DRAWREADY REPORT\n\n';
      content += JSON.stringify(data, null, 2);
    }
    
    // Change extension to .txt for better readability
    extension = 'txt';
    mimeType = 'text/plain';
    
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.${extension}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getPropertyName = (propertyId) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop ? `${prop.address}, ${prop.city}` : 'Unknown Property';
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Please log in to access reports.</p>
      </div>
    );
  }

  // Tab definitions for sidebar
  const reportTabs = [
    { id: 'properties', label: 'Properties', icon: Building2 },
    { id: 'monthly', label: 'Monthly Costs', icon: Calendar },
    { id: 'income', label: 'Income', icon: DollarSign },
    { id: 'expenses', label: 'Property Expenses', icon: TrendingUp },
    { id: 'general', label: 'Business Expenses', icon: FileText },
    { id: 'reports', label: 'Generate Reports', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex">
      {/* Left Sidebar Navigation */}
      {/* Mobile nav drawer overlay (below lg) */}
      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileNavOpen(false)} data-testid="reports-nav-overlay" />
      )}
      <aside className={`fixed inset-y-0 left-0 w-64 bg-slate-900 flex flex-col h-full z-50 transform transition-transform duration-300 lg:translate-x-0 lg:z-30 ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Close (mobile drawer only) */}
        <button onClick={() => setMobileNavOpen(false)} className="lg:hidden absolute top-4 right-3 text-slate-400 hover:text-white z-10" data-testid="reports-nav-close" aria-label="Close menu">
          <X className="w-5 h-5" />
        </button>
        {/* Logo/Brand */}
        <div className="p-4 border-b border-slate-700">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">D</span>
            </div>
            <div className="overflow-hidden">
              <h1 className="font-bold text-lg text-white whitespace-nowrap">DrawReady</h1>
              <p className="text-xs text-slate-400 whitespace-nowrap">Construction Finance</p>
            </div>
          </button>
        </div>

        {/* Page Title Section */}
        <div className="px-4 py-3 border-b border-slate-700">
          <h2 className="font-bold text-white">Financial Reports</h2>
          <p className="text-xs text-slate-400">Tax reports & properties</p>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {/* Dashboard */}
            <li>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                <LayoutDashboard className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Dashboard</span>
              </button>
            </li>
            
            {/* Report Tabs */}
            {reportTabs.map(tab => (
              <li key={tab.id}>
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <tab.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Section - User & Logout */}
        <div className="border-t border-slate-700 py-4">
          <ul className="space-y-1 px-3">
            <li>
              <button
                onClick={() => navigate('/contact')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white transition-all"
              >
                <HelpCircle className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">Help & Support</span>
              </button>
            </li>
          </ul>

          {/* User Info & Logout */}
          {user && (
            <div className="mt-4 px-3">
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-medium">
                    {(user.name || user.email || '?')[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user.name || 'User'}</p>
                  <p className="text-xs text-slate-400 truncate">{user.email}</p>
                </div>
              </div>
              <div className="mt-2" data-testid="sidebar-notifications">
                <NotificationBell userId={user.id} variant="sidebar" expanded={true} />
              </div>
              <button
                onClick={onLogout}
                className="mt-2 w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all"
              >
                <LogOut className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0 lg:ml-64">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b shadow-sm sticky top-0 z-40">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <button onClick={() => setMobileNavOpen(true)} data-testid="reports-mobile-menu-toggle" className="p-2 -ml-2 rounded-lg hover:bg-slate-100" aria-label="Open menu">
                <Menu className="w-6 h-6" />
              </button>
              <span className="font-bold text-lg text-slate-900">Financial Reports</span>
              <span className="w-6" />
            </div>
          </div>
        </div>

        {/* Page Content Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">Financial Reports</h1>
                <p className="text-blue-100 mt-1 text-xs sm:text-sm hidden sm:block">Manage properties and generate tax reports</p>
              </div>
            </div>
          </div>
        </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Nav is handled by the hamburger drawer on mobile and the sidebar on desktop */}
          <TabsList className="hidden">
            <TabsTrigger value="properties" className="gap-2">
              <Building2 className="w-4 h-4" />
              Properties
            </TabsTrigger>
            <TabsTrigger value="monthly" className="gap-2">
              <Calendar className="w-4 h-4" />
              Monthly Costs
            </TabsTrigger>
            <TabsTrigger value="income" className="gap-2">
              <DollarSign className="w-4 h-4" />
              Income
            </TabsTrigger>
            <TabsTrigger value="expenses" className="gap-2">
              <TrendingUp className="w-4 h-4" />
              Property Expenses
            </TabsTrigger>
            <TabsTrigger value="general" className="gap-2">
              <FileText className="w-4 h-4" />
              Business Expenses
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Generate Reports
            </TabsTrigger>
          </TabsList>

          {/* Properties Tab */}
          <TabsContent value="properties">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Rental Properties</h2>
              <Dialog open={isPropertyDialogOpen} onOpenChange={setIsPropertyDialogOpen}>
                <DialogTrigger asChild>
                  <Button><Plus className="w-4 h-4 mr-2" />Add Property</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add Rental Property</DialogTitle>
                    <DialogDescription>Enter property details for Form 8825 tracking</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateProperty} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2 space-y-2">
                        <Label>Street Address</Label>
                        <Input
                          value={newProperty.address}
                          onChange={(e) => setNewProperty({ ...newProperty, address: e.target.value })}
                          placeholder="123 Main St"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>City</Label>
                        <Input
                          value={newProperty.city}
                          onChange={(e) => setNewProperty({ ...newProperty, city: e.target.value })}
                          placeholder="Cedar Rapids"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2">
                          <Label>State</Label>
                          <Input
                            value={newProperty.state}
                            onChange={(e) => setNewProperty({ ...newProperty, state: e.target.value })}
                            placeholder="IA"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>ZIP</Label>
                          <Input
                            value={newProperty.zip_code}
                            onChange={(e) => setNewProperty({ ...newProperty, zip_code: e.target.value })}
                            placeholder="52403"
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Property Type</Label>
                        <Select
                          value={String(newProperty.property_type)}
                          onValueChange={(v) => setNewProperty({ ...newProperty, property_type: parseInt(v) })}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(PROPERTY_TYPES).map(([key, value]) => (
                              <SelectItem key={key} value={key}>{value}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Purchase Price ($)</Label>
                        <Input
                          type="number"
                          value={newProperty.purchase_price}
                          onChange={(e) => setNewProperty({ ...newProperty, purchase_price: e.target.value })}
                          placeholder="150000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Purchase Date</Label>
                        <Input
                          type="date"
                          value={newProperty.purchase_date}
                          onChange={(e) => setNewProperty({ ...newProperty, purchase_date: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Monthly Rent ($)</Label>
                        <Input
                          type="number"
                          value={newProperty.monthly_rent}
                          onChange={(e) => setNewProperty({ ...newProperty, monthly_rent: e.target.value })}
                          placeholder="1200"
                        />
                      </div>
                    </div>
                    
                    {/* Property Value & Loan */}
                    <div className="pt-4 border-t">
                      <h4 className="font-medium text-sm mb-3">Value & Financing</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Home Value ($)</Label>
                          <Input
                            type="number"
                            value={newProperty.home_value}
                            onChange={(e) => setNewProperty({ ...newProperty, home_value: e.target.value })}
                            placeholder="Current market value"
                          />
                          <p className="text-xs text-slate-500">Current estimated market value</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Loan Balance ($)</Label>
                          <Input
                            type="number"
                            value={newProperty.loan_balance}
                            onChange={(e) => setNewProperty({ ...newProperty, loan_balance: e.target.value })}
                            placeholder="Amount owed"
                          />
                          <p className="text-xs text-slate-500">Outstanding mortgage balance</p>
                        </div>
                      </div>
                      
                      {/* Loan Details for Transaction Matching */}
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div className="space-y-2">
                          <Label>Loan Number</Label>
                          <Input
                            type="text"
                            value={newProperty.loan_number}
                            onChange={(e) => setNewProperty({ ...newProperty, loan_number: e.target.value })}
                            placeholder="e.g., 1234567890"
                          />
                          <p className="text-xs text-slate-500">For auto-matching bank transactions</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Lender Name</Label>
                          <Input
                            type="text"
                            value={newProperty.lender_name}
                            onChange={(e) => setNewProperty({ ...newProperty, lender_name: e.target.value })}
                            placeholder="e.g., Hills Bank"
                          />
                          <p className="text-xs text-slate-500">Bank or mortgage company</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-700">
                        <strong>Tip:</strong> Monthly expenses (Principal, Interest, Taxes, Insurance) are tracked in the "Monthly Costs" tab for detailed reporting.
                      </p>
                    </div>
                    
                    <Button type="submit" className="w-full">Add Property</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {renderRepFilters(true)}
            {properties.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Building2 className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-slate-500">No properties yet. Add your first rental property.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleProperties.map((prop) => (
                  <Card key={prop.id}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-base">{prop.address}</CardTitle>
                          <CardDescription>{prop.city}, {prop.state} {prop.zip_code}</CardDescription>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEditProperty(prop)}>
                            <Edit2 className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteProperty(prop.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Type:</span>
                        <span>{PROPERTY_TYPES[prop.property_type]}</span>
                      </div>
                      {prop.purchase_date && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Purchase Date:</span>
                          <span>{formatDateMMDDYYYY(prop.purchase_date)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-500">Purchase Price:</span>
                        <span>${(prop.purchase_price || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Monthly Rent:</span>
                        <span className="text-green-600 font-medium">${(prop.monthly_rent || 0).toLocaleString()}</span>
                      </div>
                      
                      {/* Value & Equity Section */}
                      <div className="pt-2 border-t mt-2">
                        <p className="text-xs font-medium text-slate-600 mb-1">Value & Financing:</p>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Home Value:</span>
                          <span className="text-blue-600 font-medium">${(prop.home_value || prop.purchase_price || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Loan Balance:</span>
                          <span className="text-red-600">${currentLoanBalance(prop).toLocaleString()}</span>
                        </div>
                        {principalPaidFor(prop.id) > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-400">Principal paid:</span>
                            <span className="text-slate-500">-${principalPaidFor(prop.id).toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-medium pt-1 border-t border-slate-100 mt-1">
                          <span className="text-slate-600">Equity:</span>
                          <span className="text-green-600">${((prop.home_value || prop.purchase_price || 0) - currentLoanBalance(prop)).toLocaleString()}</span>
                        </div>
                      </div>
                      
                      {/* Quick Actions */}
                      <div className="pt-2 mt-2 flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => {
                            setSelectedPropertyForMonthlyCost(prop.id);
                            setIsMonthlyCostDialogOpen(true);
                          }}
                        >
                          <Calendar className="w-3 h-3 mr-1" />
                          Monthly Cost
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => {
                            setSelectedPropertyForExpense(prop.id);
                            setNewExpense({ ...newExpense, property_id: prop.id });
                            setIsExpenseDialogOpen(true);
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Expense
                        </Button>
                        <Button
                          size="sm"
                          className="w-full text-xs bg-blue-600 hover:bg-blue-700"
                          onClick={() => generatePropertyReport(prop.id)}
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          View Property Report
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Edit Property Dialog */}
          <Dialog open={isEditPropertyDialogOpen} onOpenChange={(open) => {
            setIsEditPropertyDialogOpen(open);
            if (!open) setEditingProperty(null);
          }}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Property</DialogTitle>
                <DialogDescription>Update property details</DialogDescription>
              </DialogHeader>
              {editingProperty && (
                <form onSubmit={handleUpdateProperty} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Street Address</Label>
                      <Input
                        value={editingProperty.address}
                        onChange={(e) => setEditingProperty({ ...editingProperty, address: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input
                        value={editingProperty.city}
                        onChange={(e) => setEditingProperty({ ...editingProperty, city: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label>State</Label>
                        <Input
                          value={editingProperty.state}
                          onChange={(e) => setEditingProperty({ ...editingProperty, state: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>ZIP</Label>
                        <Input
                          value={editingProperty.zip_code}
                          onChange={(e) => setEditingProperty({ ...editingProperty, zip_code: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Property Type</Label>
                      <Select
                        value={String(editingProperty.property_type)}
                        onValueChange={(v) => setEditingProperty({ ...editingProperty, property_type: parseInt(v) })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(PROPERTY_TYPES).map(([key, value]) => (
                            <SelectItem key={key} value={key}>{value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Purchase Price ($)</Label>
                      <Input
                        type="number"
                        value={editingProperty.purchase_price}
                        onChange={(e) => setEditingProperty({ ...editingProperty, purchase_price: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Purchase Date</Label>
                      <Input
                        type="date"
                        value={editingProperty.purchase_date || ''}
                        onChange={(e) => setEditingProperty({ ...editingProperty, purchase_date: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Monthly Rent ($)</Label>
                      <Input
                        type="number"
                        value={editingProperty.monthly_rent}
                        onChange={(e) => setEditingProperty({ ...editingProperty, monthly_rent: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  {/* Value & Financing */}
                  <div className="pt-4 border-t">
                    <h4 className="font-medium text-sm mb-3">Value & Financing</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Home Value ($)</Label>
                        <Input
                          type="number"
                          value={editingProperty.home_value || ''}
                          onChange={(e) => setEditingProperty({ ...editingProperty, home_value: e.target.value })}
                          placeholder="Current market value"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Loan Balance ($)</Label>
                        <Input
                          type="number"
                          value={editingProperty.loan_balance || ''}
                          onChange={(e) => setEditingProperty({ ...editingProperty, loan_balance: e.target.value })}
                          placeholder="Amount owed"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      <strong>Tip:</strong> Monthly expenses (Principal, Interest, Taxes, Insurance) are tracked in the "Monthly Costs" tab for detailed reporting.
                    </p>
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setIsEditPropertyDialogOpen(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1">Save Changes</Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>

          {/* Monthly Costs Tab */}
          <TabsContent value="monthly">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold">Monthly Cost Breakdown</h2>
                <p className="text-sm text-slate-500">Track detailed monthly costs: Principal, Interest, Insurance, Taxes, HOA, PMI</p>
              </div>
              <Button onClick={() => { setSelectedPropertyForMonthlyCost(null); setIsMonthlyCostDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />Add Monthly Costs
              </Button>
            </div>

            {renderRepFilters()}
            {monthlyCosts.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Calendar className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-slate-500">No monthly costs recorded yet.</p>
                  <p className="text-xs text-slate-400 mt-2">Track detailed monthly costs for accurate financial reporting.</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Monthly Cost Records</CardTitle>
                  <CardDescription>Detailed breakdown by month</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">Property</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Month</th>
                        <th className="px-4 py-3 text-right text-sm font-medium">Principal</th>
                        <th className="px-4 py-3 text-right text-sm font-medium">Interest</th>
                        <th className="px-4 py-3 text-right text-sm font-medium">Insurance</th>
                        <th className="px-4 py-3 text-right text-sm font-medium">Taxes</th>
                        <th className="px-4 py-3 text-right text-sm font-medium">HOA/PMI</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-blue-600">Total</th>
                        <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyRep(monthlyCosts).map((item) => {
                        const total = (item.principal || 0) + (item.interest || 0) + (item.insurance || 0) + 
                                     (item.property_tax || 0) + (item.hoa || 0) + (item.pmi || 0) + (item.other || 0);
                        return (
                          <tr key={item.id} className="border-b">
                            <td className="px-4 py-3 text-sm">{getPropertyName(item.property_id)}</td>
                            <td className="px-4 py-3 text-sm font-medium">{getMonthName(item.month)} {item.year}</td>
                            <td className="px-4 py-3 text-sm text-right">${item.principal?.toFixed(2) || '0.00'}</td>
                            <td className="px-4 py-3 text-sm text-right">${item.interest?.toFixed(2) || '0.00'}</td>
                            <td className="px-4 py-3 text-sm text-right">${item.insurance?.toFixed(2) || '0.00'}</td>
                            <td className="px-4 py-3 text-sm text-right">${item.property_tax?.toFixed(2) || '0.00'}</td>
                            <td className="px-4 py-3 text-sm text-right">${((item.hoa || 0) + (item.pmi || 0)).toFixed(2)}</td>
                            <td className="px-4 py-3 text-sm text-right font-medium text-blue-600">${total.toFixed(2)}</td>
                            <td className="px-4 py-3 text-center">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  apiService.deleteMonthlyCost(item.id).then(() => {
                                    toast.success('Monthly cost deleted');
                                    loadData();
                                  });
                                }}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
            
            {/* Import from Bank Section */}
            <Card className="mt-4 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Landmark className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-purple-800">Auto-Import from Bank <span className="ml-2 text-[11px] font-semibold uppercase tracking-wide text-purple-500 bg-purple-100 px-2 py-0.5 rounded-full">Coming Soon</span></p>
                      <p className="text-sm text-purple-600 mt-1">
                        Bank connection (ACH) is coming soon — you&apos;ll be able to link your bank and auto-import mortgage payment breakdowns.
                      </p>
                    </div>
                  </div>
                  <Button 
                    disabled
                    className="bg-purple-600 hover:bg-purple-700 opacity-60 cursor-not-allowed"
                    data-testid="import-from-bank-btn"
                  >
                    <Landmark className="w-4 h-4 mr-2" />Coming Soon
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Bank Import Dialog */}
            <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Landmark className="w-5 h-5 text-purple-600" />
                    Import Monthly Costs from Bank
                  </DialogTitle>
                  <DialogDescription>
                    Select a month to scan for mortgage-related transactions. We'll automatically categorize them for you.
                  </DialogDescription>
                </DialogHeader>

                {/* Month/Year Selector */}
                <div className="flex gap-3 items-end mb-4">
                  <div className="flex-1">
                    <Label>Month</Label>
                    <Select value={String(importMonth)} onValueChange={(v) => setImportMonth(parseInt(v))}>
                      <SelectTrigger data-testid="import-month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(12)].map((_, i) => (
                          <SelectItem key={i+1} value={String(i+1)}>{getMonthName(i+1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Year</Label>
                    <Select value={String(importYear)} onValueChange={(v) => setImportYear(parseInt(v))}>
                      <SelectTrigger data-testid="import-year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(5)].map((_, i) => {
                          const year = new Date().getFullYear() - i;
                          return <SelectItem key={year} value={String(year)}>{year}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={handleFetchTransactions} 
                    disabled={isLoadingTransactions}
                    data-testid="fetch-transactions-btn"
                  >
                    {isLoadingTransactions ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scanning...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />Scan Transactions
                      </>
                    )}
                  </Button>
                </div>

                {/* Mortgage Payment Groups */}
                {mortgageGroups.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="font-medium text-slate-800">
                      Found {mortgageGroups.length} Mortgage Payment{mortgageGroups.length !== 1 ? 's' : ''}
                    </h3>
                    {mortgageGroups.map((group, idx) => (
                      <Card key={idx} className={`border ${group.assigned ? 'border-green-300 bg-green-50' : 'border-slate-200'}`}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="text-sm text-slate-500">Transaction Date</p>
                              <p className="font-medium">{group.date}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-slate-500">Total</p>
                              <p className="font-bold text-lg text-blue-600">${group.total?.toFixed(2)}</p>
                            </div>
                          </div>

                          {/* Breakdown */}
                          <div className="grid grid-cols-3 gap-2 text-sm mb-3 bg-slate-50 p-3 rounded-lg">
                            {group.breakdown?.principal > 0 && (
                              <div>
                                <span className="text-slate-500">Principal:</span>
                                <span className="font-medium ml-1">${group.breakdown.principal.toFixed(2)}</span>
                              </div>
                            )}
                            {group.breakdown?.interest > 0 && (
                              <div>
                                <span className="text-slate-500">Interest:</span>
                                <span className="font-medium ml-1">${group.breakdown.interest.toFixed(2)}</span>
                              </div>
                            )}
                            {group.breakdown?.insurance > 0 && (
                              <div>
                                <span className="text-slate-500">Insurance:</span>
                                <span className="font-medium ml-1">${group.breakdown.insurance.toFixed(2)}</span>
                              </div>
                            )}
                            {group.breakdown?.property_tax > 0 && (
                              <div>
                                <span className="text-slate-500">Property Tax:</span>
                                <span className="font-medium ml-1">${group.breakdown.property_tax.toFixed(2)}</span>
                              </div>
                            )}
                            {group.breakdown?.hoa > 0 && (
                              <div>
                                <span className="text-slate-500">HOA:</span>
                                <span className="font-medium ml-1">${group.breakdown.hoa.toFixed(2)}</span>
                              </div>
                            )}
                            {group.breakdown?.pmi > 0 && (
                              <div>
                                <span className="text-slate-500">PMI:</span>
                                <span className="font-medium ml-1">${group.breakdown.pmi.toFixed(2)}</span>
                              </div>
                            )}
                          </div>

                          {/* Individual Transactions */}
                          <details className="mb-3">
                            <summary className="text-sm text-slate-500 cursor-pointer hover:text-slate-700">
                              View {group.transactions?.length || 0} transaction(s)
                            </summary>
                            <div className="mt-2 space-y-1 text-xs">
                              {group.transactions?.map((t, tidx) => (
                                <div key={tidx} className="flex justify-between bg-white p-2 rounded border">
                                  <span className="truncate max-w-[200px]">{t.name}</span>
                                  <span className="font-medium">${t.amount?.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </details>

                          {/* Assign to Property */}
                          {group.assigned ? (
                            <div className="flex items-center gap-2 text-green-700">
                              <CheckCircle2 className="w-4 h-4" />
                              <span className="text-sm font-medium">Imported to {getPropertyName(group.assigned_property_id)}</span>
                            </div>
                          ) : (
                            <div className="flex gap-2 items-center">
                              <Label className="text-sm whitespace-nowrap">Assign to:</Label>
                              <Select 
                                onValueChange={(propertyId) => handleAssignMortgage(group, propertyId)}
                                data-testid={`assign-property-select-${idx}`}
                              >
                                <SelectTrigger className="flex-1">
                                  <SelectValue placeholder="Select property..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {properties.map(p => (
                                    <SelectItem key={p.id} value={p.id}>
                                      {p.nickname || p.address}, {p.city}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Empty State */}
                {mortgageGroups.length === 0 && !isLoadingTransactions && (
                  <div className="text-center py-8 text-slate-500">
                    <Landmark className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>Select a month and click "Scan Transactions" to find mortgage payments.</p>
                    <p className="text-xs mt-2">We'll look for grouped transactions that match mortgage payment patterns.</p>
                  </div>
                )}

                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Income Tab */}
          <TabsContent value="income">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold">Income</h2>
                <p className="text-sm text-slate-500">Track rent, property sales, and other company income</p>
              </div>
              <Dialog open={isIncomeDialogOpen} onOpenChange={setIsIncomeDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />Record Income
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Record Income</DialogTitle>
                    <DialogDescription>Add rental income, property sale, or other business income</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateIncome} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Income Type</Label>
                      <Select
                        value={newIncome.income_type}
                        onValueChange={(v) => setNewIncome({ ...newIncome, income_type: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(INCOME_TYPE_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Property (Optional)</Label>
                      <Select
                        value={newIncome.property_id || "none"}
                        onValueChange={(v) => setNewIncome({ ...newIncome, property_id: v === "none" ? "" : v })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select property (optional)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Company-wide (no specific property)</SelectItem>
                          {properties.map((prop) => (
                            <SelectItem key={prop.id} value={prop.id}>
                              {prop.address}, {prop.city}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Amount ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newIncome.amount}
                          onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Input
                          type="date"
                          value={newIncome.date}
                          onChange={(e) => setNewIncome({ ...newIncome, date: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Description (Optional)</Label>
                      <Input
                        value={newIncome.description}
                        onChange={(e) => setNewIncome({ ...newIncome, description: e.target.value })}
                        placeholder="Monthly rent payment, property sale proceeds, etc."
                      />
                    </div>
                    <Button type="submit" className="w-full">Record Income</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Estimated Monthly Rent Summary */}
            {properties.length > 0 && (
              <Card className="mb-4 bg-green-50 border-green-200">
                <CardContent className="py-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-800">
                      <strong>Estimated Annual Rent:</strong> ${properties.reduce((sum, p) => sum + (p.monthly_rent || 0) * 12, 0).toLocaleString()} 
                      <span className="text-green-600 ml-2">({properties.length} properties × monthly rent × 12)</span>
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {renderRepFilters()}
            {income.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <DollarSign className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-slate-500">No income recorded yet.</p>
                  <p className="text-xs text-slate-400 mt-2">Record rent payments, property sales, and other income here.</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Property</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Description</th>
                        <th className="px-4 py-3 text-right text-sm font-medium">Amount</th>
                        <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyRep(income).map((item) => (
                        <tr key={item.id} className="border-b">
                          <td className="px-4 py-3 text-sm font-medium">
                            {INCOME_TYPE_LABELS[item.income_type] || item.income_type}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500">
                            {item.property_id ? getPropertyName(item.property_id) : 'Company-wide'}
                          </td>
                          <td className="px-4 py-3 text-sm">{formatDateMMDDYYYY(item.date)}</td>
                          <td className="px-4 py-3 text-sm text-slate-500">{item.description || '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-green-600 font-medium">
                            ${item.amount.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                apiService.deleteRentalIncome(item.id).then(() => {
                                  toast.success('Income deleted');
                                  loadData();
                                });
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Expenses Tab */}
          <TabsContent value="expenses">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Rental Expenses</h2>
              <Dialog open={isExpenseDialogOpen} onOpenChange={setIsExpenseDialogOpen}>
                <DialogTrigger asChild>
                  <Button disabled={properties.length === 0}>
                    <Plus className="w-4 h-4 mr-2" />Record Expense
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Record Expense</DialogTitle>
                    <DialogDescription>Add expense (categorized for Form 8825)</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateExpense} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Property</Label>
                      <Select
                        value={newExpense.property_id}
                        onValueChange={(v) => setNewExpense({ ...newExpense, property_id: v })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                        <SelectContent>
                          {properties.map((prop) => (
                            <SelectItem key={prop.id} value={prop.id}>
                              {prop.address}, {prop.city}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Category (Form 8825)</Label>
                      <Select
                        value={newExpense.category}
                        onValueChange={(v) => setNewExpense({ ...newExpense, category: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(EXPENSE_CATEGORY_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Amount ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newExpense.amount}
                          onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Input
                          type="date"
                          value={newExpense.date}
                          onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Description (Optional)</Label>
                      <Input
                        value={newExpense.description}
                        onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                        placeholder="Plumbing repair"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Receipt (Optional)</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          id="receipt-upload"
                          accept="image/*,.pdf"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setNewExpense({ ...newExpense, receipt_file: file });
                            }
                          }}
                        />
                        {newExpense.receipt_file ? (
                          <div className="flex-1 flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-md">
                            <Paperclip className="w-4 h-4 text-green-600" />
                            <span className="text-sm text-green-700 truncate flex-1">
                              {newExpense.receipt_file.name}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              onClick={() => setNewExpense({ ...newExpense, receipt_file: null })}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={() => document.getElementById('receipt-upload').click()}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Attach Receipt Photo/File
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">Supported: Images (JPG, PNG) or PDF</p>
                    </div>
                    <Button type="submit" className="w-full">Record Expense</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {renderRepFilters()}
            {expenses.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <TrendingUp className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-slate-500">No expenses recorded yet.</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">Property</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Category</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Description</th>
                        <th className="px-4 py-3 text-right text-sm font-medium">Amount</th>
                        <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyRep(expenses).map((item) => (
                        <tr key={item.id} className="border-b">
                          <td className="px-4 py-3 text-sm">{getPropertyName(item.property_id)}</td>
                          <td className="px-4 py-3 text-sm">{formatDateMMDDYYYY(item.date)}</td>
                          <td className="px-4 py-3 text-sm">{EXPENSE_CATEGORY_LABELS[item.category] || item.category}</td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="truncate max-w-[150px]">{item.description || '-'}</span>
                              {item.receipt_file_id && (
                                <span 
                                  className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded cursor-pointer hover:bg-green-100"
                                  onClick={async () => {
                                    try {
                                      const response = await apiService.getRentalExpenseReceipt(item.id);
                                      const url = URL.createObjectURL(response.data);
                                      window.open(url, '_blank');
                                    } catch (error) {
                                      toast.error('Failed to load receipt');
                                    }
                                  }}
                                >
                                  <Paperclip className="w-3 h-3" />
                                  Receipt
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-red-600 font-medium">
                            ${item.amount.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  setEditingExpense({
                                    id: item.id,
                                    property_id: item.property_id,
                                    category: item.category,
                                    amount: item.amount,
                                    description: item.description || '',
                                    date: item.date
                                  });
                                  setIsEditExpenseDialogOpen(true);
                                }}
                              >
                                <Edit2 className="w-4 h-4 text-blue-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  apiService.deleteRentalExpense(item.id).then(() => {
                                    toast.success('Expense deleted');
                                    loadData();
                                  });
                                }}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
            
            {/* Edit Expense Dialog */}
            <Dialog open={isEditExpenseDialogOpen} onOpenChange={setIsEditExpenseDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Edit Expense</DialogTitle>
                  <DialogDescription>Update the expense details</DialogDescription>
                </DialogHeader>
                {editingExpense && (
                  <form onSubmit={handleUpdateExpense} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Property</Label>
                      <Select
                        value={editingExpense.property_id}
                        onValueChange={(v) => setEditingExpense({ ...editingExpense, property_id: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {properties.map((prop) => (
                            <SelectItem key={prop.id} value={prop.id}>
                              {prop.address}, {prop.city}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select
                        value={editingExpense.category}
                        onValueChange={(v) => setEditingExpense({ ...editingExpense, category: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(EXPENSE_CATEGORY_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingExpense.amount}
                        onChange={(e) => setEditingExpense({ ...editingExpense, amount: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={editingExpense.date}
                        onChange={(e) => setEditingExpense({ ...editingExpense, date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description (Optional)</Label>
                      <Input
                        value={editingExpense.description}
                        onChange={(e) => setEditingExpense({ ...editingExpense, description: e.target.value })}
                        placeholder="Brief description"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" className="flex-1" onClick={() => setIsEditExpenseDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="flex-1">Save Changes</Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* General Business Expenses Tab */}
          <TabsContent value="general">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-semibold">General Business Expenses</h2>
                <p className="text-sm text-slate-500">Office, fuel, and other expenses not tied to a specific property</p>
              </div>
              <Dialog open={isGeneralExpenseDialogOpen} onOpenChange={setIsGeneralExpenseDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />Add Business Expense
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Business Expense</DialogTitle>
                    <DialogDescription>Record a general business expense (not property-specific)</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateGeneralExpense} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select
                        value={newGeneralExpense.category}
                        onValueChange={(v) => setNewGeneralExpense({ ...newGeneralExpense, category: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(GENERAL_EXPENSE_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Amount ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newGeneralExpense.amount}
                          onChange={(e) => setNewGeneralExpense({ ...newGeneralExpense, amount: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Input
                          type="date"
                          value={newGeneralExpense.date}
                          onChange={(e) => setNewGeneralExpense({ ...newGeneralExpense, date: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Description (Optional)</Label>
                      <Input
                        value={newGeneralExpense.description}
                        onChange={(e) => setNewGeneralExpense({ ...newGeneralExpense, description: e.target.value })}
                        placeholder="Office supplies from Staples"
                      />
                    </div>
                    <Button type="submit" className="w-full">Record Expense</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {generalExpenses.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Select value={bizRange} onValueChange={setBizRange}>
                  <SelectTrigger className="w-[150px]" data-testid="biz-expense-range"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                    <SelectItem value="quarter">This Quarter</SelectItem>
                    <SelectItem value="year">This Year</SelectItem>
                    <SelectItem value="lastyear">Last Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={bizProject} onValueChange={setBizProject}>
                  <SelectTrigger className="w-[180px]" data-testid="biz-expense-project"><SelectValue placeholder="All Projects" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                  </SelectContent>
                </Select>
                {bizRange === 'custom' && (
                  <>
                    <Input type="date" value={bizCustom.start} onChange={(e) => setBizCustom((s) => ({ ...s, start: e.target.value }))} className="w-[150px]" data-testid="biz-expense-custom-start" />
                    <Input type="date" value={bizCustom.end} onChange={(e) => setBizCustom((s) => ({ ...s, end: e.target.value }))} className="w-[150px]" data-testid="biz-expense-custom-end" />
                  </>
                )}
              </div>
            )}
            {visibleBusinessExpenses.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <FileText className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-slate-500">No general business expenses recorded yet.</p>
                  <p className="text-xs text-slate-400 mt-2">Track office supplies, fuel, software, and other business costs here.</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Category</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Description</th>
                        <th className="px-4 py-3 text-right text-sm font-medium">Amount</th>
                        <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleBusinessExpenses.map((item) => (
                        <tr key={item.id} className="border-b">
                          <td className="px-4 py-3 text-sm">{formatDateMMDDYYYY(item.date)}</td>
                          <td className="px-4 py-3 text-sm">{GENERAL_EXPENSE_LABELS[item.category] || item.category}</td>
                          <td className="px-4 py-3 text-sm text-slate-500">{item.description || '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-red-600 font-medium">
                            ${item.amount.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex justify-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  setEditingGeneralExpense(item);
                                  setIsEditGeneralExpenseDialogOpen(true);
                                }}
                              >
                                <Edit2 className="w-4 h-4 text-blue-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  apiService.deleteGeneralExpense(item.id).then(() => {
                                    toast.success('Expense deleted');
                                    loadData();
                                  });
                                }}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Report Period Selection */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Report Period</CardTitle>
                  <CardDescription>Select the time period for your financial reports</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-4">
                    <div className="space-y-2">
                      <Label>Period Type</Label>
                      <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                        <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yearly">Full Year</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="semi-annual">Semi-Annual</SelectItem>
                          <SelectItem value="ytd">Year-to-Date</SelectItem>
                          <SelectItem value="custom">Custom Range</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Year</Label>
                      <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                        <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[2023, 2024, 2025, 2026].map((y) => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {(selectedPeriod === 'quarterly' || selectedPeriod === 'semi-annual') && (
                      <div className="space-y-2">
                        <Label>{selectedPeriod === 'quarterly' ? 'Quarter' : 'Half'}</Label>
                        <Select value={String(selectedQuarter)} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
                          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {selectedPeriod === 'quarterly' ? (
                              <>
                                <SelectItem value="1">Q1 (Jan-Mar)</SelectItem>
                                <SelectItem value="2">Q2 (Apr-Jun)</SelectItem>
                                <SelectItem value="3">Q3 (Jul-Sep)</SelectItem>
                                <SelectItem value="4">Q4 (Oct-Dec)</SelectItem>
                              </>
                            ) : (
                              <>
                                <SelectItem value="1">H1 (Jan-Jun)</SelectItem>
                                <SelectItem value="3">H2 (Jul-Dec)</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {selectedPeriod === 'custom' && (
                      <>
                        <div className="space-y-2">
                          <Label>Start Date</Label>
                          <Input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className="w-40"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>End Date</Label>
                          <Input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            className="w-40"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      <strong>Selected Period:</strong> {getDateRange().periodLabel}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Form 8825 */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    IRS Form 8825
                  </CardTitle>
                  <CardDescription>Rental Real Estate Income and Expenses</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button onClick={generateForm8825} className="w-full">
                    Generate Form 8825 Data
                  </Button>
                  {form8825Data && (
                    <div className="space-y-3 text-sm">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium">Year: {form8825Data.year}</p>
                        <p>Properties: {form8825Data.properties.length}</p>
                        <p className="text-green-600">Total Gross Rents: ${form8825Data.totals.total_gross_rents.toLocaleString()}</p>
                        <p className="text-red-600">Total Expenses: ${form8825Data.totals.total_expenses.toLocaleString()}</p>
                        <p className={form8825Data.totals.net_rental_income >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                          Net Income: ${form8825Data.totals.net_rental_income.toLocaleString()}
                        </p>
                      </div>
                      <Button variant="outline" size="sm" data-testid="export-form8825-pdf" onClick={() => downloadPdf(apiService.getForm8825Pdf(user.id, selectedYear), `Form_8825_${selectedYear}.pdf`)}>
                        <Download className="w-4 h-4 mr-2" />Download PDF
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Profit & Loss */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Profit & Loss Statement
                  </CardTitle>
                  <CardDescription>Income vs Expenses Summary</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button onClick={generateProfitLoss} className="w-full">
                    Generate P&L Statement
                  </Button>
                  {profitLossData && (
                    <div className="space-y-3 text-sm">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium">{profitLossData.period_name || profitLossData.period_label || `${profitLossData.quarter ? `Q${profitLossData.quarter}` : 'Full Year'} ${profitLossData.year}`}</p>
                        <p className="text-green-600">Total Income: ${profitLossData.income.total.toLocaleString()}</p>
                        <p className="text-red-600">Total Expenses: ${profitLossData.expenses.total.toLocaleString()}</p>
                        <p className={profitLossData.net_profit >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                          Net Profit: ${profitLossData.net_profit.toLocaleString()}
                        </p>
                        <p className="text-slate-500">Margin: {profitLossData.profit_margin}%</p>
                      </div>
                      <Button variant="outline" size="sm" data-testid="export-pnl-pdf" onClick={() => downloadPdf(apiService.getProfitLossPdf(user.id, selectedYear, selectedPeriod === 'quarterly' ? selectedQuarter : null), `Profit_Loss_${selectedYear}.pdf`)}>
                        <Download className="w-4 h-4 mr-2" />Download PDF
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Balance Sheet */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5" />
                    Balance Sheet
                  </CardTitle>
                  <CardDescription>Assets, Liabilities, Equity</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button onClick={generateBalanceSheet} className="w-full">
                    Generate Balance Sheet
                  </Button>
                  {balanceSheetData && (
                    <div className="space-y-3 text-sm">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium">Properties: {balanceSheetData.property_count}</p>
                        <p>Real Estate Value: ${balanceSheetData.assets.real_estate.toLocaleString()}</p>
                        <p className="text-slate-500">Accum. Depreciation: ${Math.abs(balanceSheetData.assets.accumulated_depreciation).toLocaleString()}</p>
                        <p className="font-bold">Net Assets: ${balanceSheetData.total_assets.toLocaleString()}</p>
                        <p className={balanceSheetData.equity.retained_earnings >= 0 ? 'text-green-600' : 'text-red-600'}>
                          Retained Earnings: ${balanceSheetData.equity.retained_earnings.toLocaleString()}
                        </p>
                      </div>
                      <Button variant="outline" size="sm" data-testid="export-balance-sheet-pdf" onClick={() => downloadPdf(apiService.getBalanceSheetPdf(user.id), 'Balance_Sheet.pdf')}>
                        <Download className="w-4 h-4 mr-2" />Download PDF
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quarterly Report */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Quarterly Compile Statement
                  </CardTitle>
                  <CardDescription>Property-by-property quarterly summary</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button onClick={generateQuarterly} className="w-full">
                    Generate Quarterly Report
                  </Button>
                  {quarterlyData && (
                    <div className="space-y-3 text-sm">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium">{quarterlyData.quarter_name}</p>
                        <p className="text-green-600">Total Income: ${quarterlyData.summary.total_income.toLocaleString()}</p>
                        <p className="text-red-600">Total Expenses: ${quarterlyData.summary.total_expenses.toLocaleString()}</p>
                        <p className={quarterlyData.summary.net_income >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                          Net Income: ${quarterlyData.summary.net_income.toLocaleString()}
                        </p>
                      </div>
                      <Button variant="outline" size="sm" data-testid="export-quarterly-pdf" onClick={() => downloadPdf(apiService.getQuarterlyPdf(user.id, selectedYear, (selectedPeriod === 'quarterly' || selectedPeriod === 'semi-annual') ? selectedQuarter : null), `Compile_Statement_${selectedYear}.pdf`)}>
                        <Download className="w-4 h-4 mr-2" />Download PDF
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Overall Business Report */}
              <Card className="md:col-span-2 border-2 border-blue-200 bg-blue-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <TrendingUp className="w-5 h-5" />
                    Overall Business Report
                  </CardTitle>
                  <CardDescription>Net rental profit + other income vs additional expenses</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Year-End Review Prompt */}
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-800">Year-End Review Reminder</p>
                        <p className="text-amber-700 mt-1">
                          Before generating your report, please review each property's monthly costs to ensure accuracy:
                        </p>
                        <ul className="mt-2 space-y-1 text-amber-700">
                          <li className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            <strong>Principal & Interest</strong> - rates may have changed if refinanced
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            <strong>Property Taxes</strong> - escrow amounts often change annually
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            <strong>Insurance</strong> - may have switched carriers or rates changed
                          </li>
                        </ul>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-3 border-amber-300 text-amber-700 hover:bg-amber-100"
                          onClick={() => setActiveTab('properties')}
                        >
                          Review Property Costs
                        </Button>
                      </div>
                    </div>
                  </div>

                  <Button onClick={generateOverall} className="w-full bg-blue-600 hover:bg-blue-700">
                    Generate Overall Report
                  </Button>
                  {overallData && (
                    <div className="space-y-3 text-sm">
                      <div className="p-4 bg-white rounded-lg border">
                        <div className="flex justify-between items-center mb-3">
                          <p className="font-bold text-lg">Year {overallData.year} Summary</p>
                          <span className="text-xs text-slate-500">{overallData.property_count} properties</span>
                        </div>
                        
                        {/* Income & Expenses Summary */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 bg-green-50 rounded-lg">
                            <p className="text-slate-500 text-xs uppercase">Total Income</p>
                            <p className="text-green-600 text-2xl font-bold">
                              ${((overallData.income.gross_annual_rent || 0) + (overallData.income.recorded_income || 0)).toLocaleString()}
                            </p>
                            <div className="mt-2 text-xs space-y-1">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Gross Rental Income:</span>
                                <span className="text-green-600">${overallData.income.gross_annual_rent?.toLocaleString() || 0}</span>
                              </div>
                              {overallData.income.recorded_income > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Other Income (Sales, Fees):</span>
                                  <span className="text-green-600">${overallData.income.recorded_income?.toLocaleString() || 0}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="p-3 bg-red-50 rounded-lg">
                            <p className="text-slate-500 text-xs uppercase">Total Expenses</p>
                            <p className="text-red-600 text-2xl font-bold">
                              ${((overallData.expenses.recurring_property_costs?.total || 0) + (overallData.expenses.rental_expenses?.total || 0) + (overallData.expenses.general_expenses?.total || 0)).toLocaleString()}
                            </p>
                            <div className="mt-2 text-xs space-y-1">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Monthly Recurring:</span>
                                <span className="text-red-500">${overallData.expenses.recurring_property_costs?.total?.toLocaleString() || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Property Expenses:</span>
                                <span className="text-red-500">${overallData.expenses.rental_expenses?.total?.toLocaleString() || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Business Expenses:</span>
                                <span className="text-red-500">${overallData.expenses.general_expenses?.total?.toLocaleString() || 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Net Income */}
                        <div className="mt-4 pt-4 border-t">
                          {(() => {
                            const totalIncome = (overallData.income.gross_annual_rent || 0) + (overallData.income.recorded_income || 0);
                            const totalExpenses = (overallData.expenses.recurring_property_costs?.total || 0) + 
                                                  (overallData.expenses.rental_expenses?.total || 0) + 
                                                  (overallData.expenses.general_expenses?.total || 0);
                            const netProfit = totalIncome - totalExpenses;
                            const profitMargin = totalIncome > 0 ? ((netProfit / totalIncome) * 100).toFixed(1) : 0;
                            return (
                              <>
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-lg">Net Profit:</span>
                                  <span className={`text-3xl font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    ${netProfit.toLocaleString()}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-500 mt-1">Profit Margin: <strong>{profitMargin}%</strong></p>
                              </>
                            );
                          })()}
                        </div>
                        
                        {/* Monthly Recurring Expenses Breakdown */}
                        {overallData.expenses.recurring_property_costs?.total > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="font-medium text-slate-700 mb-3">Monthly Recurring Expenses (Annual):</p>
                            <div className="p-3 bg-red-50 rounded-lg text-xs space-y-1">
                              <div className="flex justify-between">
                                <span className="text-slate-600">Principal & Interest:</span>
                                <span className="text-red-600">${overallData.expenses.recurring_property_costs?.breakdown?.principal_interest?.toLocaleString() || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-600">Property Tax:</span>
                                <span className="text-red-600">${overallData.expenses.recurring_property_costs?.breakdown?.property_tax?.toLocaleString() || 0}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-600">Insurance:</span>
                                <span className="text-red-600">${overallData.expenses.recurring_property_costs?.breakdown?.insurance?.toLocaleString() || 0}</span>
                              </div>
                              {overallData.expenses.recurring_property_costs?.breakdown?.hoa > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-slate-600">HOA:</span>
                                  <span className="text-red-600">${overallData.expenses.recurring_property_costs?.breakdown?.hoa?.toLocaleString() || 0}</span>
                                </div>
                              )}
                              {overallData.expenses.recurring_property_costs?.breakdown?.pmi > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-slate-600">PMI:</span>
                                  <span className="text-red-600">${overallData.expenses.recurring_property_costs?.breakdown?.pmi?.toLocaleString() || 0}</span>
                                </div>
                              )}
                              <div className="flex justify-between font-medium pt-1 border-t border-red-200">
                                <span>Total Monthly Recurring:</span>
                                <span className="text-red-700">${overallData.expenses.recurring_property_costs?.total?.toLocaleString() || 0}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Additional Expense Breakdown */}
                        {(overallData.expenses.rental_expenses?.total > 0 || overallData.expenses.general_expenses?.total > 0) && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="font-medium text-slate-700 mb-3">Additional Expense Breakdown:</p>
                            <div className="space-y-2">
                              {/* Additional Rental Expenses */}
                              {overallData.expenses.rental_expenses?.total > 0 && (
                                <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                                  <div>
                                    <span className="font-medium">Property Expenses</span>
                                    <p className="text-xs text-slate-500">Repairs, maintenance, etc.</p>
                                  </div>
                                  <span className="text-red-600 font-bold">${overallData.expenses.rental_expenses?.total?.toLocaleString() || 0}</span>
                                </div>
                              )}
                              {/* Business Expenses */}
                              {overallData.expenses.general_expenses?.total > 0 && (
                                <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                                  <div>
                                    <span className="font-medium">Business Expenses</span>
                                    <p className="text-xs text-slate-500">Office, fuel, professional services</p>
                                  </div>
                                  <span className="text-red-600 font-bold">${overallData.expenses.general_expenses?.total?.toLocaleString() || 0}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Income Breakdown (if available) */}
                        {overallData.income.by_type && Object.keys(overallData.income.by_type).length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="font-medium text-slate-700 mb-2">Other Income by Type:</p>
                            <div className="grid grid-cols-2 gap-2">
                              {Object.entries(overallData.income.by_type).map(([type, amount]) => (
                                <div key={type} className="flex justify-between text-sm">
                                  <span className="text-slate-600 capitalize">{type.replace('_', ' ')}:</span>
                                  <span className="text-green-600">${amount.toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => exportToCsv(overallData, `overall_report_${selectedYear}`, 'csv')}>
                          <Download className="w-4 h-4 mr-2" />Export CSV
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => exportToCsv(overallData, `overall_report_${selectedYear}`, 'json')}>
                          <Download className="w-4 h-4 mr-2" />Export JSON
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Project Expenses Summary */}
              {projectExpenses.length > 0 && (
                <Card className="md:col-span-2 border-2 border-purple-200 bg-purple-50/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-purple-800">
                      <Building2 className="w-5 h-5" />
                      Construction Project Expenses
                    </CardTitle>
                    <CardDescription>Money spent on development projects (not included in rental reports)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(() => {
                        // Filter by date range and group by project
                        const { startDate, endDate, periodLabel } = getDateRange();
                        const filteredExpenses = projectExpenses.filter(e => {
                          const expDate = e.date || '';
                          return expDate >= startDate && expDate <= endDate;
                        });
                        
                        const byProject = {};
                        filteredExpenses.forEach(e => {
                          const key = e.project_name || 'Unknown Project';
                          if (!byProject[key]) byProject[key] = { total: 0, expenses: [] };
                          byProject[key].total += e.amount || 0;
                          byProject[key].expenses.push(e);
                        });
                        
                        const totalProjectExpenses = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
                        
                        return (
                          <>
                            <div className="bg-white rounded-lg border p-3">
                              <p className="text-sm font-medium text-purple-800 mb-2">{periodLabel}</p>
                              {Object.entries(byProject).map(([projectName, data]) => (
                                <div key={projectName} className="flex justify-between items-center py-2 border-b last:border-0">
                                  <div>
                                    <span className="font-medium">{projectName}</span>
                                    <p className="text-xs text-slate-500">{data.expenses.length} expense(s)</p>
                                  </div>
                                  <span className="text-purple-600 font-bold">${data.total.toLocaleString()}</span>
                                </div>
                              ))}
                              {Object.keys(byProject).length === 0 && (
                                <p className="text-slate-500 text-sm">No project expenses in this period</p>
                              )}
                            </div>
                            {totalProjectExpenses > 0 && (
                              <div className="bg-purple-100 rounded-lg p-3 flex justify-between items-center">
                                <span className="font-medium text-purple-800">Total Project Expenses:</span>
                                <span className="text-xl font-bold text-purple-600">${totalProjectExpenses.toLocaleString()}</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Global Monthly Cost Dialog - accessible from any tab */}
        <Dialog open={isMonthlyCostDialogOpen} onOpenChange={setIsMonthlyCostDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Record Monthly Costs</DialogTitle>
              <DialogDescription>Enter the detailed breakdown of costs for a specific month</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateMonthlyCost} className="space-y-4">
              <div className="space-y-2">
                <Label>Property</Label>
                <Select
                  value={selectedPropertyForMonthlyCost || ''}
                  onValueChange={setSelectedPropertyForMonthlyCost}
                >
                  <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent>
                    {properties.map((prop) => (
                      <SelectItem key={prop.id} value={prop.id}>
                        {prop.address}, {prop.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Select
                    value={String(newMonthlyCost.year)}
                    onValueChange={(v) => setNewMonthlyCost({ ...newMonthlyCost, year: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2023, 2024, 2025, 2026].map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select
                    value={String(newMonthlyCost.month)}
                    onValueChange={(v) => setNewMonthlyCost({ ...newMonthlyCost, month: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="border rounded-lg p-3 bg-slate-50">
                <p className="text-sm font-medium mb-3 text-slate-700">Cost Breakdown</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Principal</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="160.15"
                      value={newMonthlyCost.principal}
                      onChange={(e) => setNewMonthlyCost({ ...newMonthlyCost, principal: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Interest</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="522.39"
                      value={newMonthlyCost.interest}
                      onChange={(e) => setNewMonthlyCost({ ...newMonthlyCost, interest: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Insurance</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="83.25"
                      value={newMonthlyCost.insurance}
                      onChange={(e) => setNewMonthlyCost({ ...newMonthlyCost, insurance: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Property Tax</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="90.21"
                      value={newMonthlyCost.property_tax}
                      onChange={(e) => setNewMonthlyCost({ ...newMonthlyCost, property_tax: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">HOA</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={newMonthlyCost.hoa}
                      onChange={(e) => setNewMonthlyCost({ ...newMonthlyCost, hoa: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">PMI</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={newMonthlyCost.pmi}
                      onChange={(e) => setNewMonthlyCost({ ...newMonthlyCost, pmi: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Other Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={newMonthlyCost.other}
                      onChange={(e) => setNewMonthlyCost({ ...newMonthlyCost, other: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Other Description</Label>
                    <Input
                      placeholder="e.g., Late fee"
                      value={newMonthlyCost.other_description}
                      onChange={(e) => setNewMonthlyCost({ ...newMonthlyCost, other_description: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Input
                  placeholder="Any additional notes"
                  value={newMonthlyCost.notes}
                  onChange={(e) => setNewMonthlyCost({ ...newMonthlyCost, notes: e.target.value })}
                />
              </div>
              
              {/* Total calculation */}
              <div className="bg-blue-50 rounded-lg p-3 flex justify-between items-center">
                <span className="font-medium text-blue-800">Monthly Total:</span>
                <span className="text-xl font-bold text-blue-600">
                  ${(
                    (parseFloat(newMonthlyCost.principal) || 0) +
                    (parseFloat(newMonthlyCost.interest) || 0) +
                    (parseFloat(newMonthlyCost.insurance) || 0) +
                    (parseFloat(newMonthlyCost.property_tax) || 0) +
                    (parseFloat(newMonthlyCost.hoa) || 0) +
                    (parseFloat(newMonthlyCost.pmi) || 0) +
                    (parseFloat(newMonthlyCost.other) || 0)
                  ).toFixed(2)}
                </span>
              </div>
              
              <Button type="submit" className="w-full">Save Monthly Costs</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Per-Property Report Dialog */}
        <Dialog open={isPropertyReportDialogOpen} onOpenChange={setIsPropertyReportDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Property Financial Report
                </DialogTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadPropertyReport}
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </Button>
              </div>
              <DialogDescription>
                {selectedPropertyForReport?.address}, {selectedPropertyForReport?.city}, {selectedPropertyForReport?.state}
              </DialogDescription>
            </DialogHeader>
            
            {/* Year Selector */}
            <div className="flex items-center gap-4 mb-4">
              <Label>Report Year:</Label>
              <Select value={String(propertyReportYear)} onValueChange={(v) => {
                setPropertyReportYear(parseInt(v));
                if (selectedPropertyForReport) {
                  generatePropertyReport(selectedPropertyForReport.id);
                }
              }}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({length: 5}, (_, i) => new Date().getFullYear() - i).map(yr => (
                    <SelectItem key={yr} value={String(yr)}>{yr}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {propertyReportData && (
              <div className="space-y-6">
                {/* Property Summary */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-800 mb-3">Property Summary</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Purchase Price</p>
                      <p className="font-medium">${(selectedPropertyForReport?.purchase_price || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Current Value</p>
                      <p className="font-medium text-blue-600">${(selectedPropertyForReport?.home_value || selectedPropertyForReport?.purchase_price || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Monthly Rent</p>
                      <p className="font-medium text-green-600">${(selectedPropertyForReport?.monthly_rent || 0).toLocaleString()}/mo</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Equity</p>
                      <p className="font-medium">${((selectedPropertyForReport?.home_value || selectedPropertyForReport?.purchase_price || 0) - (selectedPropertyForReport?.loan_balance || 0)).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                
                {/* Income & Expenses Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-green-600 mb-1">Total Income</p>
                    <p className="text-2xl font-bold text-green-700">${(propertyReportData.totalIncome || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <p className="text-sm text-red-600 mb-1">Total Expenses</p>
                    <p className="text-2xl font-bold text-red-700">${(propertyReportData.totalExpenses || 0).toLocaleString()}</p>
                  </div>
                  <div className={`rounded-lg p-4 text-center ${(propertyReportData.netIncome || 0) >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                    <p className={`text-sm mb-1 ${(propertyReportData.netIncome || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Net Income</p>
                    <p className={`text-2xl font-bold ${(propertyReportData.netIncome || 0) >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                      ${(propertyReportData.netIncome || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
                
                {/* Income Breakdown */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Income Breakdown</h3>
                  <div className="bg-white border rounded-lg">
                    {propertyReportData.profitLoss?.income_breakdown && Object.entries(propertyReportData.profitLoss.income_breakdown).length > 0 ? (
                      <table className="w-full text-sm">
                        <tbody>
                          {Object.entries(propertyReportData.profitLoss.income_breakdown).map(([type, amount]) => (
                            <tr key={type} className="border-b last:border-0">
                              <td className="px-4 py-2 text-slate-600 capitalize">{type.replace(/_/g, ' ')}</td>
                              <td className="px-4 py-2 text-right font-medium text-green-600">${Number(amount).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : propertyReportData.totalIncome > 0 ? (
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b last:border-0">
                            <td className="px-4 py-2 text-slate-600">Rental Income (Estimated)</td>
                            <td className="px-4 py-2 text-right font-medium text-green-600">${propertyReportData.totalIncome.toLocaleString()}</td>
                          </tr>
                          <tr className="bg-slate-50">
                            <td className="px-4 py-2 text-xs text-slate-500" colSpan="2">
                              * Based on ${selectedPropertyForReport?.monthly_rent?.toLocaleString()}/mo × {propertyReportYear === new Date().getFullYear() ? new Date().getMonth() + 1 : 12} months
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-center text-slate-400 py-4">No income recorded for {propertyReportYear}</p>
                    )}
                  </div>
                </div>
                
                {/* Monthly Recurring Costs (P&I, Insurance, Taxes, etc.) */}
                {propertyReportData.monthlyCosts && propertyReportData.monthlyCosts.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-3">Monthly Recurring Costs ({propertyReportData.monthlyCosts.length} months)</h3>
                    <div className="bg-white border rounded-lg">
                      <table className="w-full text-sm">
                        <tbody>
                          {(() => {
                            const totals = propertyReportData.monthlyCosts.reduce((acc, mc) => ({
                              principal: acc.principal + (parseFloat(mc.principal) || 0),
                              interest: acc.interest + (parseFloat(mc.interest) || 0),
                              property_tax: acc.property_tax + (parseFloat(mc.property_tax) || 0),
                              insurance: acc.insurance + (parseFloat(mc.insurance) || 0),
                              hoa: acc.hoa + (parseFloat(mc.hoa) || 0),
                              pmi: acc.pmi + (parseFloat(mc.pmi) || 0),
                              other: acc.other + (parseFloat(mc.other) || 0)
                            }), { principal: 0, interest: 0, property_tax: 0, insurance: 0, hoa: 0, pmi: 0, other: 0 });
                            
                            return (
                              <>
                                {totals.principal > 0 && (
                                  <tr className="border-b">
                                    <td className="px-4 py-2 text-slate-600">Mortgage Principal</td>
                                    <td className="px-4 py-2 text-right font-medium text-red-600">${totals.principal.toLocaleString()}</td>
                                  </tr>
                                )}
                                {totals.interest > 0 && (
                                  <tr className="border-b">
                                    <td className="px-4 py-2 text-slate-600">Mortgage Interest</td>
                                    <td className="px-4 py-2 text-right font-medium text-red-600">${totals.interest.toLocaleString()}</td>
                                  </tr>
                                )}
                                {totals.property_tax > 0 && (
                                  <tr className="border-b">
                                    <td className="px-4 py-2 text-slate-600">Property Taxes</td>
                                    <td className="px-4 py-2 text-right font-medium text-red-600">${totals.property_tax.toLocaleString()}</td>
                                  </tr>
                                )}
                                {totals.insurance > 0 && (
                                  <tr className="border-b">
                                    <td className="px-4 py-2 text-slate-600">Insurance</td>
                                    <td className="px-4 py-2 text-right font-medium text-red-600">${totals.insurance.toLocaleString()}</td>
                                  </tr>
                                )}
                                {totals.hoa > 0 && (
                                  <tr className="border-b">
                                    <td className="px-4 py-2 text-slate-600">HOA Fees</td>
                                    <td className="px-4 py-2 text-right font-medium text-red-600">${totals.hoa.toLocaleString()}</td>
                                  </tr>
                                )}
                                {totals.pmi > 0 && (
                                  <tr className="border-b">
                                    <td className="px-4 py-2 text-slate-600">PMI</td>
                                    <td className="px-4 py-2 text-right font-medium text-red-600">${totals.pmi.toLocaleString()}</td>
                                  </tr>
                                )}
                                {totals.other > 0 && (
                                  <tr className="border-b">
                                    <td className="px-4 py-2 text-slate-600">Other</td>
                                    <td className="px-4 py-2 text-right font-medium text-red-600">${totals.other.toLocaleString()}</td>
                                  </tr>
                                )}
                                <tr className="bg-red-50 font-semibold">
                                  <td className="px-4 py-2 text-slate-700">Total Recurring Costs</td>
                                  <td className="px-4 py-2 text-right text-red-700">
                                    ${(totals.principal + totals.interest + totals.property_tax + totals.insurance + totals.hoa + totals.pmi + totals.other).toLocaleString()}
                                  </td>
                                </tr>
                              </>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Other Expense Breakdown (non-recurring) */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Other Expenses</h3>
                  <div className="bg-white border rounded-lg">
                    {propertyReportData.profitLoss?.expenses_breakdown && Object.entries(propertyReportData.profitLoss.expenses_breakdown).length > 0 ? (
                      (() => {
                        // Filter out recurring costs that are shown separately
                        const recurringKeys = ['mortgage_principal', 'mortgage_interest', 'property_taxes', 'insurance', 'hoa_fees', 'pmi', 'other_recurring'];
                        const otherExpenses = Object.entries(propertyReportData.profitLoss.expenses_breakdown)
                          .filter(([category]) => !recurringKeys.includes(category));
                        
                        if (otherExpenses.length === 0) {
                          return <p className="text-center text-slate-400 py-4">No other expenses recorded for {propertyReportYear}</p>;
                        }
                        
                        return (
                          <table className="w-full text-sm">
                            <tbody>
                              {otherExpenses.map(([category, amount]) => (
                                <tr key={category} className="border-b last:border-0">
                                  <td className="px-4 py-2 text-slate-600">{EXPENSE_CATEGORY_LABELS[category] || category}</td>
                                  <td className="px-4 py-2 text-right font-medium text-red-600">${Number(amount).toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        );
                      })()
                    ) : (
                      <p className="text-center text-slate-400 py-4">No expenses recorded for {propertyReportYear}</p>
                    )}
                  </div>
                </div>
                
                {/* Recent Transactions */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Recent Expenses ({propertyReportData.expenseRecords?.length || 0})</h3>
                  <div className="bg-white border rounded-lg max-h-48 overflow-y-auto">
                    {propertyReportData.expenseRecords && propertyReportData.expenseRecords.length > 0 ? (
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-slate-600">Date</th>
                            <th className="px-3 py-2 text-left text-slate-600">Category</th>
                            <th className="px-3 py-2 text-left text-slate-600">Description</th>
                            <th className="px-3 py-2 text-right text-slate-600">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {propertyReportData.expenseRecords.map((exp, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="px-3 py-2">{exp.month}/{exp.year}</td>
                              <td className="px-3 py-2 capitalize">{exp.category}</td>
                              <td className="px-3 py-2 text-slate-500">{exp.description || '-'}</td>
                              <td className="px-3 py-2 text-right font-medium">${(exp.amount || 0).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-center text-slate-400 py-4">No expense records for {propertyReportYear}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsPropertyReportDialogOpen(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit General Expense Dialog */}
        <Dialog open={isEditGeneralExpenseDialogOpen} onOpenChange={setIsEditGeneralExpenseDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Business Expense</DialogTitle>
              <DialogDescription>Update the expense details</DialogDescription>
            </DialogHeader>
            {editingGeneralExpense && (
              <form onSubmit={handleUpdateGeneralExpense} className="space-y-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select 
                    value={editingGeneralExpense.category} 
                    onValueChange={(v) => setEditingGeneralExpense({...editingGeneralExpense, category: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(GENERAL_EXPENSE_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Amount ($)</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={editingGeneralExpense.amount} 
                    onChange={(e) => setEditingGeneralExpense({...editingGeneralExpense, amount: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input 
                    value={editingGeneralExpense.description || ''} 
                    onChange={(e) => setEditingGeneralExpense({...editingGeneralExpense, description: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input 
                    type="date"
                    value={editingGeneralExpense.date?.split('T')[0] || ''} 
                    onChange={(e) => setEditingGeneralExpense({...editingGeneralExpense, date: e.target.value})}
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setIsEditGeneralExpenseDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1">Save Changes</Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </main>
      </div>{/* End of main content area */}
    </div>
  );
}

export default Reports;
